import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Award,
  Trophy,
  Lock,
  MessageSquare,
  FileText,
  GraduationCap,
  RotateCcw,
  Loader2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Building2,
  User,
  Info,
  Clock,
  ShieldAlert,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import type { AIAgent, Message, Feedback, Scenario, Session, Target, Persona } from "../types";
import { SessionQuotaBlockedDialog } from "../components/SessionQuotaBlockedDialog";
import { trainingApi, accountApi, authApi, challengesApi, certificationPathsApi, relanceApi, planApi, type RelanceCardData } from "../lib/api";
import { RelanceCard } from "../components/RelanceCard";
import { SubTabs } from "../components/SubTabs";
import { CaseDetailDialog } from "../components/CaseDetailDialog";
import { RetrainFocusBanner, FocusDeltaChip } from "../components/RetrainFocusBanner";
import { DuelFrame } from "../components/DuelFrame";
import { Celebration } from "../components/Celebration";
import { objectiveLabel } from "../lib/objective-label";
import { pickScoringLabel, pickVerdictLabel } from "../lib/scoring-i18n";
import { DrillIcon } from "../components/DrillIcon";
import { drillSessionApi } from "../lib/api";
import { launchDrill, lowest3Drillable } from "../lib/drill-launch";
import { useRealtimeSession, type RealtimeMessage } from "../hooks/useRealtimeSession";
import { CallStage } from "../components/CallStage";
import { LiveFeedbackPanel, useLiveFeedbackValues } from "../components/LiveFeedbackPanel";
import { useTranslation } from "react-i18next";
import { pickCasLegacyField } from "../lib/banque-de-cas/i18n";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";
import { pickPersonaField } from "../lib/persona-i18n";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickOfferLabel, pickOfferDescription } from "../lib/offer-i18n";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { pickDisplayIndustry, buildBriefFromVariables, applySpokenBrief } from "../lib/scenario-structured";
import { briefLabelsForLanguage } from "../lib/scenario-brief-labels";
import { ScenarioBrief } from "../components/ScenarioBrief";
import { pickDecisionMaker, getPersonaLabel, getPersonaPersonalityLevels } from "../components/PersonaUtils";
import { localizeMaturityLevel, localizeInternalPressure } from "../lib/persona-personality";
import { formatTime } from "../i18n/formatters";
import { useDrillVisible } from "../hooks/useDrillVisible";
import { useSidebandVisible } from "../hooks/useSidebandVisible";
import { isSidebandVisible } from "../lib/sideband-visibility";

// Simple markdown renderer (bold, line breaks, lists) — HTML-safe
function SimpleMarkdown({ text, className }: { text: string; className?: string }) {
  // Escape HTML entities first to prevent XSS, then apply markdown formatting
  const html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n- /g, "\n<li>")
    .replace(/<li>(.+?)(?=\n|$)/g, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gs, "<ul class='list-disc pl-4 mt-1'>$1</ul>")
    .replace(/\n/g, "<br>");
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

// (Le brief de session est rendu par <ScenarioBrief>, partagé avec l'historique.)

// Mode mock : simule l'appel avec des messages mockés (pas d'audio réel)
// Ajout ?mode=mock dans l'URL pour forcer le mode démo
const MOCK_AUDIO_MODE = new URLSearchParams(window.location.search).get("mode") === "mock";

interface TrainingPageProps {
  agent: AIAgent;
  challengeId?: string;
  onBack: () => void;
  onComplete: (feedback: Feedback) => void;
  onSessionActive?: (active: boolean) => void;
  onNavigate?: (page: import("../types").PageType) => void; // « S'exercer » : route vers la page d'exercice
}

type TrainingPhase = "init" | "target-select" | "difficulty-select" | "scenario-loading" | "ready" | "calling" | "feedback-loading" | "feedback" | "coaching" | "done";

export function TrainingPage({ agent, challengeId, onBack, onComplete, onSessionActive, onNavigate }: TrainingPageProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const getLabel = (c: { label?: string; labelFr?: string; labelEn?: string }) => pickScoringLabel(c, lang);

  /**
   * Les critères que CE cas sur-pondère, en libellés lisibles.
   *
   * `evalCriteriaOverrides.activated` porte des CLÉS (`clarte_objectif_appel`) ; les libellés
   * vivent dans la table des critères, déjà chargée pour le bandeau du simulateur. On résout via
   * `pickScoringLabel` — jamais le champ brut, sous peine d'afficher du français en mode anglais.
   *
   * Une clé absente du référentiel du simulateur est IGNORÉE plutôt qu'affichée telle quelle :
   * « clarte_objectif_appel » ne veut rien dire pour un commercial.
   */
  /**
   * La phrase qui dit POURQUOI ce cas est conseillé.
   *
   * Elle nomme une compétence, JAMAIS une note : c'est la règle anti-triche du plan de
   * rétention — un motif qui citerait un score inviterait à jouer contre le score.
   */
  const phraseRecommandation = (motif: { cle: string; critere?: string }, criteres: any[]): string => {
    if (motif.cle === "weakness") {
      const c = criteres.find((x: any) => x.key === motif.critere);
      // Sans libellé lisible, on retombe sur un motif générique : « clarte_objectif_appel »
      // ne veut rien dire pour un commercial.
      const label = c ? pickScoringLabel(c, lang) : "";
      return label ? t("training.recoWeakness", { critere: label }) : t("training.recoNeverPlayed");
    }
    if (motif.cle === "neverPlayed") return t("training.recoNeverPlayed");
    if (motif.cle === "rightLevel") return t("training.recoRightLevel");
    return t("training.recoReference");
  };

  const criteresSurPonderes = (cas: any, criteres: any[]): string[] => {
    // L'accent appartient au COUPLE cas × simulateur (migration 20260914-1110) : un socle se joue
    // sur plusieurs simulateurs et chacun a le sien. Repli sur l'accent du cas pour les cas
    // clients et ceux issus d'un débrief Sales Coach, qui n'ont pas de couple en base.
    const cles: string[] = (cas?.accentsParSimulateur?.[agent.id]
      ?? cas?.evalCriteriaOverrides?.activated ?? []) as string[];
    if (!Array.isArray(cles) || cles.length === 0) return [];
    return cles
      .map((k) => criteres.find((c: any) => c.key === k))
      .filter(Boolean)
      .map((c: any) => pickScoringLabel(c, lang))
      .filter((l: string) => l.trim().length > 0);
  };
  const agentColor = agent.color || "#7CBF94";

  // Session state
  const [session, setSession] = useState<Session | null>(null);
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [phase, setPhase] = useState<TrainingPhase>("init");
  const [error, setError] = useState<string | null>(null);
  // Phase A 2026-05-14 — erreur audio visible côté user (au lieu de silent log).
  // Permet d'afficher un dialog avec retry quand mic refusé / Realtime down.
  const [realtimeError, setRealtimeError] = useState<string | null>(null);
  const [quotaReached, setQuotaReached] = useState<{ used: number; max: number } | null>(null);
  const [sessionQuotaBlocked, setSessionQuotaBlocked] = useState<import("../components/SessionQuotaBlockedDialog").QuotaBlockedInfo | null>(null);

  // Target & difficulty state
  const [allTargets, setAllTargets] = useState<Target[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<Target | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  const [targetSelectStep, setTargetSelectStep] = useState<"target" | "persona">("target");
  // Toggle dans le sélecteur de cas : "Cas d'équipe" (publics) vs "Cas personnels"
  // (privés Sales Coach Réel + Prep Meeting). Par défaut : équipe. Le toggle n'est
  // affiché que si les 2 catégories ont du contenu — sinon on montre seul ce qui existe.
  // Onglet de provenance ouvert (null = le premier qui a du contenu) et cas affiché en popup.
  const [ongletCas, setOngletCas] = useState<string | null>(null);
  const [casOuvert, setCasOuvert] = useState<any>(null);
  // Fork d'un cas Kestio en cours (id du cas source), pour désactiver le bouton pendant la copie.
  const [forkEnCours, setForkEnCours] = useState<string | null>(null);
  // S'approprier un cas est un geste de conception : un commercial ne le voit pas. Mêmes profils
  // que la garde serveur — afficher un bouton qui répondrait 403 serait pire que pas de bouton.
  const [peutForker, setPeutForker] = useState(false);
  useEffect(() => {
    authApi.me()
      .then((u: any) => setPeutForker(["ORG", "ADM", "COO", "MNG"].includes(u?.profileType)))
      .catch(() => setPeutForker(false));
  }, []);
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null);
  const [trainingOffers, setTrainingOffers] = useState<any[]>([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState<any[]>([]);
  // LE cas mis en avant pour ce simulateur, et pourquoi. Calculé côté serveur : il a besoin du
  // profil de compétence et de l'historique de l'utilisateur. `null` est un résultat normal —
  // sans raison solide, aucune carte n'est mise en avant (une recommandation permanente n'en
  // est plus une, l'œil apprend à l'ignorer).
  const [recommandation, setRecommandation] = useState<{ targetId: string; motif: { rang: number; cle: string; critere?: string } } | null>(null);

  // Call state
  const [isMuted, setIsMuted] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  // Flux audio temps réel de l'agent (waveform). Alimenté par onAgentAudioStream.
  const [agentStream, setAgentStream] = useState<MediaStream | null>(null);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);

  // Challenge timer thresholds (minutes)
  const [orangeThreshold, setOrangeThreshold] = useState<number | null>(null);
  const [redThreshold, setRedThreshold] = useState<number | null>(null);
  // Parcours certifiants — contexte de phase (bandeau + objectifs + progression).
  const [pathContext, setPathContext] = useState<any | null>(null);
  const [challengeTitle, setChallengeTitle] = useState<string>("");
  const [challengeProgress, setChallengeProgress] = useState<any | null>(null);
  const [phaseResult, setPhaseResult] = useState<any | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // UI state
  const [mainTab, setMainTab] = useState<"prospect" | "coach">("prospect");
  const [subTab, setSubTab] = useState<"scenario" | "call">("scenario");
  const [coachSubTab, setCoachSubTab] = useState<"feedback" | "exchanges">("feedback");
  const [coachMessages, setCoachMessages] = useState<Message[]>([]);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // Relance intelligente (Étape 5) — carte de prochaine action en pied de bilan.
  const [relanceCard, setRelanceCard] = useState<RelanceCardData | null>(null);
  const [relanceBusy, setRelanceBusy] = useState(false);
  const [showReplayFallback, setShowReplayFallback] = useState(false);
  // Duel (Étape 8) — contexte de passe (cadre « DUEL » brillant plein écran).
  const [duelContext, setDuelContext] = useState<{ duelId?: string; opponentName?: string; stakeCoins?: number; difficulty?: string } | null>(null);
  // Re-train ciblé (Étape 6) — critère travaillé (bandeau « focus verrouillé »)
  // + delta du critère avant/après (chip au bilan).
  const [retrainFocus, setRetrainFocus] = useState<{ key: string; labelFr: string; labelEn: string } | null>(null);
  const [focusDelta, setFocusDelta] = useState<{ before: number | null; after: number | null; delta: number | null } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Ref to call endCall from onDisconnected (avoids circular dep)
  const endCallRef = useRef<() => void>(() => {});
  const endCallCalledRef = useRef(false);
  const isCoachingRef = useRef(false);
  // Contexte coaching (Prep Meeting / Coach Debrief) du dernier scénario lancé,
  // conservé pour que « Rejouer » relance directement le même cas au lieu de la liste.
  const lastCoachingContextRef = useRef<{
    offerId?: string; personaId?: string; focus?: string;
    difficulty?: string; privateTargetId?: string; createdAt?: number;
  } | null>(null);
  // Contexte de phase de parcours certifiant, conservé pour que « Rejouer » relance
  // directement la même phase avec son cas imposé (au lieu d'ouvrir la liste des cas
  // d'équipe). Cf. Mariam 08/07/26 : le rejouer perdait le contexte parcours.
  const lastPathPhaseRef = useRef<{
    pathPhaseId?: string; targetId?: string; simulatorId?: string; createdAt?: number;
  } | null>(null);
  // Track whether callStartTime has been set for this call (prevents multiple sets)
  const callStartTimeSetRef = useRef(false);
  // Clé éphémère OpenAI de la session vocale en cours (= agentConfig.client_secret.value).
  // Mémorisée au lancement (startCall) pour être relayée au sideband une fois le
  // call_id capturé par le hook (callback onCallId, asynchrone). Best-effort.
  const ephemeralKeyRef = useRef<string | null>(null);
  // Ref to track phase without stale closure issues
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Inactivity detection (coaching phase)
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const AUTO_CLOSE_DELAY = 60 * 1000; // 1 minute after warning
  // Filets phase APPEL (calling) — voir plus bas. Deux seuils distincts :
  const CALLING_INACTIVITY_TIMEOUT = 90 * 1000; // 90 s de silence TOTAL → session abandonnée → auto-fin
  const GOODBYE_HANGUP_DELAY = 30 * 1000; // 30 s après des politesses de fin détectées (le modèle n'a pas raccroché) → auto-fin
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goodbyeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endCoachingRef = useRef<() => void>(() => {});

  // Realtime session hook
  const handleRealtimeMessage = useCallback((msg: RealtimeMessage) => {
    const message: Message = {
      id: msg.id,
      sessionId: session?.id || "",
      content: msg.content,
      role: msg.role === "user" ? "user" : "agent",
      timestamp: msg.timestamp,
      // Reconstruction champ par champ : tout ce qui n'est pas recopié ici est perdu
      // sans le moindre signal. L'`itemId` doit traverser jusqu'à `saveMessages`.
      itemId: msg.itemId,
    };
    // Route messages: coaching phase → coachMessages, otherwise → messages
    if (isCoachingRef.current) {
      setCoachMessages((prev) => [...prev, message]);
    } else {
      setMessages((prev) => [...prev, message]);
    }
  }, [session?.id]);

  const handleRealtimeError = useCallback((err: Error) => {
    // Phase A 2026-05-14 — on log toujours, mais on affiche aussi côté user
    // pour les erreurs critiques (mic, connexion). Les erreurs transitoires
    // (network glitch, audio interruption brève) sont filtrées pour éviter
    // le bruit visuel.
    const msg = err.message || String(err);
    console.warn("[training] Realtime error:", msg);

    // Filtre des erreurs transitoires connues (à ignorer côté UI). Ajout
    // 2026-06-04 : "active response in progress" est une notice d'OpenAI
    // Realtime sur une race condition VAD/response — récupérable et non
    // pertinente côté user (filet de sécurité ; le vrai fix est
    // interrupt_response:true côté useRealtimeSession).
    const isTransient = /transcript|audio_buffer|interrupted|session\.update|active response in progress|response\.create/i.test(msg);
    if (isTransient) return;

    // Cas critiques visibles user : mic refusé, connexion impossible
    let userMessage = t("training.audioErrorGeneric");
    if (/permission|notallowed|microphone|getusermedia/i.test(msg)) {
      userMessage = t("training.audioErrorMicDenied");
    } else if (/network|connection|fetch|websocket/i.test(msg)) {
      userMessage = t("training.audioErrorNetwork");
    } else if (/token|auth|unauthorized/i.test(msg)) {
      userMessage = t("training.audioErrorSession");
    }
    setRealtimeError(userMessage);
  }, [t]);

  const handleAgentDisconnect = useCallback(() => {
    // Agent hung up or connection dropped — auto-trigger endCall if in calling phase
    // Uses phaseRef (not phase) to avoid stale closure
    // IMPORTANT: do NOT set endCallCalledRef here — endCall sets it itself
    if (!endCallCalledRef.current && phaseRef.current === "calling") {
      // Agent disconnected, auto-triggering endCall
      endCallRef.current();
    }
  }, []);

  const realtimeSession = useRealtimeSession({
    onMessage: handleRealtimeMessage,
    onError: handleRealtimeError,
    onDisconnected: handleAgentDisconnect,
    onMuteChange: (muted) => setIsMuted(muted),
    onUserSpeechStarted: () => {
      // Activité user pendant l'appel → réarme le filet d'inactivité en phase calling.
      lastActivityRef.current = Date.now();
      if (!callStartTimeSetRef.current) {
        callStartTimeSetRef.current = true;
        setCallStartTime(new Date());
      }
    },
    onAgentAudioStream: setAgentStream,
    // Sideband : une fois la session OpenAI ouverte et le call_id capturé, on
    // relaie au back (POST /api/sideband) avec la clé éphémère mémorisée au
    // lancement + le nom du simulateur. Best-effort / NON bloquant — un échec
    // (call_id absent, contrôleur down, session manquante) n'interrompt pas
    // l'appel vocal (postSideband swallow déjà ses erreurs).
    onCallId: (callId: string) => {
      // Garde-fou : le sideband pilote UNIQUEMENT le simulateur, JAMAIS le coach /
      // débrief. En phase coaching (isCoachingRef), on ne relaie rien au contrôleur.
      if (isCoachingRef.current) return;
      // Interrupteur fermé (`sideband-visibility.ts`) : aucun relais.
      if (!isSidebandVisible()) return;
      const sessionId = session?.id;
      const ephemeralKey = ephemeralKeyRef.current;
      if (!sessionId || !ephemeralKey) return;
      void trainingApi.postSideband({
        sessionId,
        call_id: callId,
        ephemeralKey,
        simulatorName: agent.name,
      });
    },
    // DIAGNOSTIC (observabilité) — remonte POURQUOI chaque simu vocale se termine
    // (raccrochage vs coupure technique WebRTC). Best-effort, n'impacte pas la simu.
    onEndDiagnostic: (diag) => {
      const sid = session?.id;
      if (sid) trainingApi.postRealtimeDiagnostic(sid, { ...diag, phase: phaseRef.current });
    },
  });

  // Feedback progressif : values 0-10 par criterionKey, alimentées via SSE une fois
  // le back BizCoach de réception du webhook N8N chuchoteur en place. Pour l'instant :
  // hook stub qui renvoie {} → toutes les barres du panneau LiveFeedbackPanel restent grises.
  // Interrupteur fermé (`sideband-visibility.ts`) : ni flux ni panneau.
  const sidebandVisible = useSidebandVisible();
  const liveFeedbackValues = useLiveFeedbackValues(sidebandVisible ? session?.id : null);

  // Signal session active to parent (for persistent session support)
  useEffect(() => {
    const active = phase === "calling" || phase === "feedback-loading" || phase === "coaching";
    onSessionActive?.(active);
  }, [phase]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);



  // Fetch challenge thresholds if in challenge context
  useEffect(() => {
    if (!challengeId) return;
    challengesApi.getLeaderboard(challengeId)
      .then((res) => {
        const ch = res.challenge;
        if (ch.title) setChallengeTitle(ch.title);
        if (ch.orangeThresholdMinutes) setOrangeThreshold(ch.orangeThresholdMinutes);
        if (ch.redThresholdMinutes) setRedThreshold(ch.redThresholdMinutes);
      })
      .catch(() => {}); // non-blocking
    challengesApi.myProgress(challengeId).then(setChallengeProgress).catch(() => {});
  }, [challengeId]);

  // Challenge — après le débrief, met à jour la progression et célèbre quand le
  // nombre de sessions requis vient d'être atteint.
  useEffect(() => {
    if (!feedback || !challengeId) return;
    challengesApi.myProgress(challengeId)
      .then((res) => {
        setChallengeProgress(res);
        if (res.minSessions > 0 && res.sessionsCount === res.minSessions) setCelebrate(true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback, challengeId]);

  // Parcours — après le débrief, récupère la progression de la phase (utile pour
  // l'affichage même avant la fin du coaching).
  useEffect(() => {
    if (!feedback || !pathContext?.pathPhaseId) return;
    certificationPathsApi.phaseProgress(pathContext.pathPhaseId)
      .then(setPhaseResult)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback, pathContext]);

  // Parcours — déclenche la célébration UNIQUEMENT quand le coaching est
  // terminé (phase === "done"), pas dès que le feedback arrive. Sinon le popup
  // « Phase réussie 🎉 » s'ouvre pendant que le coach est encore en train de
  // délivrer son retour vocal (bug remonté par Mariam 06/07/26).
  useEffect(() => {
    if (phase !== "done" || phaseResult?.status !== "done") return;
    setCelebrate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, phaseResult]);

  // Timer tick during calling phase
  useEffect(() => {
    if (phase !== "calling" || !callStartTime) {
      setElapsedSeconds(0);
      return;
    }
    const tick = () => setElapsedSeconds(Math.floor((Date.now() - callStartTime.getTime()) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [phase, callStartTime]);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, [agent.id]);

  const initializeSession = async () => {
    try {
      setPhase("init");
      setError(null);

      // Phase A 2026-05-14 — Lecture précoce du contexte coaching (depuis
      // PrepareTrainingFromCoachDialog). Si présent, on saute target-select
      // et difficulty-select dès qu'on a trouvé le target/persona matchant.
      // NOTE : on ne consomme PAS le localStorage ici — c'est handleDifficultySelect
      // qui le fait au moment de passer coachingFocus à N8N.
      let coachingCtx: { offerId?: string; personaId?: string; difficulty?: string; focus?: string; createdAt?: number; privateTargetId?: string } | null = null;
      try {
        const raw = localStorage.getItem("bizcoach_pending_coaching_context");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && (!parsed.createdAt || Date.now() - parsed.createdAt < 30 * 60 * 1000)) {
            coachingCtx = parsed;
          }
        }
      } catch { /* noop */ }

      // Parcours certifiants — contexte de phase (depuis la vue apprenant) :
      // on tague la session avec la phase et on pré-sélectionne son cas.
      let pendingPathPhase: { pathPhaseId?: string; targetId?: string; simulatorId?: string; createdAt?: number } | null = null;
      try {
        const raw = localStorage.getItem("bizcoach_pending_path_phase");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.simulatorId === agent.id && (!parsed.createdAt || Date.now() - parsed.createdAt < 30 * 60 * 1000)) {
            pendingPathPhase = parsed;
            setPathContext(parsed); // persiste pour le bandeau / les objectifs / la progression
            lastPathPhaseRef.current = parsed; // pour que « Rejouer » relance la même phase (cf. resetSession)
          } else {
            localStorage.removeItem("bizcoach_pending_path_phase");
            lastPathPhaseRef.current = null;
          }
        } else {
          lastPathPhaseRef.current = null;
        }
      } catch { /* noop */ }

      // Duel (Étape 8) — contexte de passe : rattache la session au duel + cadre « DUEL ».
      let pendingDuel: { duelId?: string; simulatorId?: string; difficulty?: string; opponentName?: string; stakeCoins?: number; createdAt?: number } | null = null;
      try {
        const raw = localStorage.getItem("bizcoach_pending_duel");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.simulatorId === agent.id && (!parsed.createdAt || Date.now() - parsed.createdAt < 30 * 60 * 1000)) {
            pendingDuel = parsed;
            setDuelContext(parsed);
          } else {
            localStorage.removeItem("bizcoach_pending_duel");
          }
        }
      } catch { /* noop */ }

      // « Tes séances » (spec/seances.md) — une séance lancée depuis l'accueil :
      // cas imposé (campagne, évaluation) et/ou re-train ciblé (session à
      // rejouer + critère). Même contrat que les clés pending ci-dessus.
      let pendingSeance: { seanceId?: string; simulatorId?: string; targetId?: string | null; sourceSessionId?: string | null; focusCriterionKey?: string | null; createdAt?: number } | null = null;
      try {
        const raw = localStorage.getItem("bizcoach_pending_seance");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.simulatorId === agent.id && (!parsed.createdAt || Date.now() - parsed.createdAt < 30 * 60 * 1000)) {
            pendingSeance = parsed;
          } else {
            localStorage.removeItem("bizcoach_pending_seance");
          }
        }
      } catch { /* noop */ }
      const seanceFocus = pendingSeance?.sourceSessionId && pendingSeance.focusCriterionKey
        ? { focusCriterionKey: pendingSeance.focusCriterionKey, sourceSessionId: pendingSeance.sourceSessionId }
        : undefined;

      // Create session
      const newSession = await trainingApi.createSession(agent.id, challengeId, pendingPathPhase?.pathPhaseId, seanceFocus, pendingDuel?.duelId, pendingSeance?.seanceId);
      setSession(newSession);
      if (pendingDuel) localStorage.removeItem("bizcoach_pending_duel");
      if (seanceFocus) {
        // Le bandeau de re-train se lit comme depuis le débrief (cf. relaunchSameCase).
        relanceApi.getFocus(newSession.id).then((r) => setRetrainFocus(r.focus)).catch(() => {});
      }

      // Load targets with personae + evaluation criteria du simulateur (pour le bandeau de sélection)
      try {
        const [avecReco, offers, criteria] = await Promise.all([
          accountApi.getTargetsWithRecommendation(agent.id),
          accountApi.getOffers().catch(() => []),
          accountApi.getEvaluationCriteria({ simulatorId: agent.id }).catch(() => []),
        ]);
        // `as any` comme le faisait `getTargets()` avant : tout l'aval de ce bloc indexe
        // `targets[0]` sans garde, et le typer plus finement demanderait de reprendre un code
        // qui n'a rien à voir avec la recommandation.
        const targets = avecReco.targets as any;
        setRecommandation(avecReco.recommandation ?? null);
        setTrainingOffers(offers || []);
        setEvaluationCriteria(criteria || []);

        // Phase de parcours : cas imposé → on le sélectionne et on saute la
        // sélection de cas (la session est déjà taguée path_phase_id).
        if (pendingPathPhase?.targetId) {
          let tgt: Target | undefined = (targets || []).find((tt: Target) => tt.id === pendingPathPhase!.targetId);
          // Cas-limite : le cas imposé n'est pas visible via getTargets (ex. cas
          // privé du manager) → endpoint dédié, garanti pour l'apprenant inscrit.
          if (!tgt && pendingPathPhase.pathPhaseId) {
            try {
              const phaseTargets = await certificationPathsApi.phaseCase(pendingPathPhase.pathPhaseId);
              tgt = (phaseTargets || []).find((tt: any) => tt.id === pendingPathPhase!.targetId) || (phaseTargets || [])[0];
            } catch { /* fallback flux normal */ }
          }
          if (tgt && (tgt.personae || []).length > 0) {
            localStorage.removeItem("bizcoach_pending_path_phase");
            setAllTargets([tgt]);
            setSelectedTarget(tgt);
            setSelectedPersona(tgt.personae![0]);
            setPhase("difficulty-select");
            return;
          }
        }

        // Séance de l'accueil : cas imposé → pré-sélection, puis la difficulté.
        if (pendingSeance?.targetId) {
          let tgt: Target | undefined = (targets || []).find((tt: Target) => tt.id === pendingSeance!.targetId);
          // Cas posé par une campagne ou cas générique : pas forcément dans la
          // liste → endpoint dédié, garanti pour la personne à qui la séance est servie.
          if (!tgt && pendingSeance.seanceId) {
            try {
              const found = await planApi.seanceCase(pendingSeance.seanceId);
              tgt = (found || []).find((tt: any) => tt.id === pendingSeance!.targetId) || (found || [])[0];
            } catch { /* repli : flux normal */ }
          }
          localStorage.removeItem("bizcoach_pending_seance");
          if (tgt && (tgt.personae || []).length > 0) {
            setAllTargets([tgt]);
            setSelectedTarget(tgt);
            setSelectedPersona(tgt.personae![0]);
            setPhase("difficulty-select");
            return;
          }
        } else if (pendingSeance) {
          localStorage.removeItem("bizcoach_pending_seance");
        }

        // Duel (Étape 8) : cas imposé aux DEUX joueurs → pré-sélection + difficulté du duel.
        if (pendingDuel?.targetId) {
          const tgt = (targets || []).find((tt: Target) => tt.id === pendingDuel!.targetId);
          if (tgt && (tgt.personae || []).length > 0) {
            setAllTargets([tgt]);
            setSelectedTarget(tgt);
            setSelectedPersona(tgt.personae![0]);
            if (pendingDuel.difficulty) setSelectedDifficulty(pendingDuel.difficulty);
            setPhase("difficulty-select");
            return;
          }
        }

        if (targets && targets.length > 0) {
          // Filtre cas de simulation pour ce simulateur — règle "intention ORG d'abord" :
          //   1. Si target.simulatorId === agent.id → toutes les personae OK (l'ORG a lié exprès ce cas à ce simulateur)
          //   2. Si target.simulatorId pointe vers un autre simulateur → exclu
          //   3. Si target.simulatorId est null (ancien cas avant le wizard) → fallback filtre tolérant par category
          // (Auparavant: filtre strict `persona.category === agent.category` qui excluait à tort les personae légitimes
          //  clonées d'une autre catégorie ou avec category vide. Cause documentée du message récurrent
          //  "Aucune persona disponible pour ce simulateur".)
          const agentCategory = agent.category || "sales";
          const filteredTargets = targets
            .map((t: Target) => {
              // Banque de cas : un cas socle est jouable sur plusieurs simulateurs, à des degrés
              // différents (natif / adaptable / dégradé / exclu). `simulatorId` ne porte que le
              // natif principal : sans lire la matrice, on n'en verrait qu'un sur sept.
              // On ne propose que `native` et `adaptable` — un cas dégradé mesurerait mal.
              const fit = (t as any).simulatorFits?.find((f: any) => f.simulatorId === agent.id);
              if (fit) {
                const jouable = fit.fitLevel === "native" || fit.fitLevel === "adaptable";
                return { ...t, personae: jouable ? (t.personae || []) : [] };
              }
              if (t.simulatorId) {
                // Cas lié explicitement à un simulateur
                if (t.simulatorId !== agent.id) return { ...t, personae: [] };
                return { ...t, personae: t.personae || [] };
              }
              // Ancien cas non lié à un simulateur : filtre tolérant par category
              return {
                ...t,
                personae: (t.personae || []).filter((p: Persona) => (p.category || "sales") === agentCategory),
              };
            })
            .filter((t: Target) => (t.personae || []).length > 0)
            // Sales Coach Réel — quand on vient d'un popup "s'entraîner sur un point"
            // (coachingCtx présent), on restreint la liste de repli :
            //  (a) exclut les cas privés Prep Meeting (private SANS sourceRealCallSessionId)
            //  (b) ne garde que les cas portant l'offre choisie dans le popup
            // Hors flux Sales Coach (coachingCtx absent), aucun changement.
            //
            // 2026-06-10 — Exception pour Coach Prepa : le cas privé Prep Meeting
            // qu'on vient explicitement de créer (identifié par coachingCtx.privateTargetId)
            // DOIT passer le filtre, sinon on l'exclut à tort et TrainingPage affiche
            // "Aucune persona disponible pour ce simulateur".
            .filter((t: Target) => {
              if (!coachingCtx?.offerId) return true;
              // Le target explicitement créé pour ce flow (CD ou CP) passe toujours.
              if (coachingCtx.privateTargetId && t.id === coachingCtx.privateTargetId) return true;
              const isPrepMeetingCase = t.visibility === "private" && !t.sourceRealCallSessionId;
              if (isPrepMeetingCase) return false;
              return (t.offerIds ?? []).includes(coachingCtx.offerId);
            });

          setAllTargets(filteredTargets);

          // Phase A 2026-05-14 — Si on vient d'un débrief Sales Coach Réel
          // (coachingCtx présent avec offerId + personaId), on saute la
          // target-select ET la difficulty-select. Le user a déjà tout choisi
          // dans la modal, inutile de lui redemander.
          //
          // 2026-06-09 — Priorité au privateTargetId (le cas privé qui vient
          // juste d'être créé par createCoachingBriefAsPrivateTarget). Sans cette
          // priorité, depuis le fix M↔N de Valérie (commit 093429b), la persona
          // est attachée à plusieurs cas (ancien + chaque nouveau privé créé) :
          // .find() tombait sur le premier match listé par la DB (souvent un
          // ancien target sans industry/companySize), donc le payload N8N
          // ratait ces champs.
          if (coachingCtx?.offerId && coachingCtx?.personaId && filteredTargets.length > 0) {
            const privateTargetId = coachingCtx.privateTargetId;
            const matchTarget =
              (privateTargetId ? filteredTargets.find((t: Target) => t.id === privateTargetId) : null) ??
              filteredTargets.find((t: Target) =>
                (t.offerIds ?? []).includes(coachingCtx!.offerId!) &&
                (t.personae ?? []).some((p: Persona) => p.id === coachingCtx!.personaId)
              );
            const matchPersona = matchTarget?.personae?.find((p: Persona) => p.id === coachingCtx!.personaId) ?? null;
            if (matchTarget && matchPersona) {
              setSelectedTarget(matchTarget);
              setSelectedPersona(matchPersona);
              const ctxDifficulty = coachingCtx.difficulty ?? "medium";
              setSelectedDifficulty(ctxDifficulty);
              // On utilise newSession (local), matchPersona et matchTarget
              // (override) pour bypasser la dépendance aux states React qui
              // n'ont pas encore commit. Sans le matchTarget passé explicitement,
              // selectedTarget reste null au moment du fetch et le backend
              // tombait sur persona.targetId legacy (cf log requestedTargetId=none).
              await runScenarioGeneration(newSession, ctxDifficulty, matchPersona, matchTarget);
              return;
            }
            // Si pas de match (offre/persona supprimée entre-temps), on fallback
            // sur le flow normal de sélection.
          }

          if (filteredTargets.length === 0) {
            // No compatible persona → show error
            setError(t("account.noPersonaeForSimulator"));
            setPhase("target-select");
          } else if (filteredTargets.length === 1 && (filteredTargets[0].personae || []).length === 1) {
            // Auto-select: 1 target + 1 persona
            setSelectedTarget(filteredTargets[0]);
            setSelectedPersona(filteredTargets[0].personae![0]);
            setPhase("difficulty-select");
          } else if (filteredTargets.length === 1) {
            // 1 target, multiple personae → skip to persona step
            setSelectedTarget(filteredTargets[0]);
            setTargetSelectStep("persona");
            setPhase("target-select");
          } else {
            // Multiple targets → show target selection
            setTargetSelectStep("target");
            setPhase("target-select");
          }
        } else {
          // No targets at all → go straight to difficulty (no persona)
          setAllTargets([]);
          setPhase("difficulty-select");
        }
      } catch {
        // Target is optional, continue without it
        setPhase("difficulty-select");
      }
    } catch (err: any) {
      if (err?.code === "SIMULATION_QUOTA_REACHED") {
        setQuotaReached({ used: err.used, max: err.max });
        return;
      }
      // Trial : limite d'essai atteinte → reload pour que le TrialBanner
      // affiche le popup de décision (activer / décliner). Cf. Phase 6.
      // ⚠️ SAUF si l'essai a déjà été DÉCLINÉ : il n'y a plus de décision à prendre, le bandeau
      // d'essai ne rend rien, et recharger la page laissait l'utilisateur devant un écran MUET.
      // On affiche alors le message du serveur (« la durée de votre essai est terminée… »).
      if (err?.code === "TRIAL_LIMIT_REACHED") {
        if (err?.declined) {
          // Texte composé ICI : le serveur ne sait pas traduire (module i18n côté navigateur).
          setError(
            err?.reason === "trial_limit_reached"
              ? (err.timeExpired ? t("quota.trialTimeExpired") : t("quota.trialSessionsExhausted"))
              : err instanceof Error ? err.message : t("training.initError"),
          );
          setPhase("init");
          return;
        }
        window.location.reload();
        return;
      }
      if (err?.code === "SESSION_QUOTA_EXCEEDED") {
        setSessionQuotaBlocked({
          used: err.used,
          limit: err.limit,
          totalPacksRemaining: err.totalPacksRemaining,
          ctaUrl: err.ctaUrl,
          envMode: err.envMode,
          cycleStart: err.cycleStart,
          cycleEnd: err.cycleEnd,
          reason: err.reason,
          subscriptionState: err.subscriptionState,
          subscriptionEndedAt: err.subscriptionEndedAt,
          subscriptionResumesAt: err.subscriptionResumesAt,
          subscriptionSource: err.subscriptionSource,
          userProfileType: err.userProfileType,
        });
        setPhase("init");
        return;
      }
      const message = err instanceof Error ? err.message : t("training.initError");
      setError(message);
      setPhase("init");
    }
  };

  const handleTargetSelect = (target: Target) => {
    setSelectedTarget(target);
    const personae = target.personae || [];
    if (personae.length === 1) {
      // Single persona → auto-select and skip to difficulty
      setSelectedPersona(personae[0]);
      setPhase("difficulty-select");
    } else {
      setTargetSelectStep("persona");
    }
  };

  const handlePersonaSelect = (persona: Persona) => {
    setSelectedPersona(persona);
    setPhase("difficulty-select");
  };

  // Phase A 2026-05-14 — Génération scénario factorisée pour pouvoir l'appeler
  // soit depuis le bouton difficulty (path classique), soit depuis l'auto-skip
  // après modal coaching (path coaching-brief où la session n'est pas encore
  // settled dans le state React).
  //
  // 2026-06-09 — Ajout du param `targetOverride` pour bypasser le state
  // selectedTarget (idem persona). Le path coaching-brief appelle cette fonction
  // SYNCHRONE juste après setSelectedTarget(matchTarget) → React n'a pas encore
  // commit le state, donc selectedTarget est encore null. Du coup le targetId
  // n'arrivait pas au backend (cf log "[scenario] requestedTargetId=none")
  // et le backend tombait sur persona.targetId legacy → industry/companySize
  // lus depuis le mauvais cas → payload N8N sans ces champs.
  const runScenarioGeneration = async (
    sessionForGen: Session,
    difficulty: string,
    persona: Persona | null,
    targetOverride?: Target | null,
  ) => {
    try {
      setPhase("scenario-loading");

      // Récupération du contexte de coaching éventuel (issu d'un débrief
      // Sales Coach Réel via PrepareTrainingFromCoachDialog). One-shot :
      // on consomme et on clear immédiatement pour ne pas polluer la prochaine session.
      let coachingFocus: string | undefined;
      let coachingOfferId: string | undefined;
      try {
        const raw = localStorage.getItem("bizcoach_pending_coaching_context");
        if (raw) {
          const ctx = JSON.parse(raw) as { offerId?: string; personaId?: string; focus?: string; difficulty?: string; privateTargetId?: string; createdAt?: number };
          if (ctx && (!ctx.createdAt || Date.now() - ctx.createdAt < 30 * 60 * 1000)) {
            coachingFocus = ctx.focus;
            coachingOfferId = ctx.offerId;
            // Mémorise le contexte pour permettre à « Rejouer » de relancer
            // directement le même cas (Prep Meeting / Coach Debrief).
            lastCoachingContextRef.current = ctx;
          } else {
            lastCoachingContextRef.current = null;
          }
          localStorage.removeItem("bizcoach_pending_coaching_context");
        } else {
          // Session classique (cas choisi dans la liste) : pas de replay direct.
          lastCoachingContextRef.current = null;
        }
      } catch { /* noop */ }

      // Cas choisi (M<->N) : on prend targetOverride en priorité (path
      // coaching-brief avec setState pas encore commit), sinon le state.
      const effectiveTarget = targetOverride ?? selectedTarget;

      // 2026-06-11 — Fallback coachingFocus depuis le cas privé sélectionné.
      // Si l'user n'est pas passé par les popups Coach Debrief / Coach Prepa
      // (donc localStorage vide) MAIS a choisi un cas privé directement depuis
      // l'onglet "Mes cas personnels" du simulateur, on récupère le focus
      // stocké en base sur le target lui-même. Priorité à scenarioInstruction
      // (version IA-enrichie, ce que les popups envoient déjà), fallback sur
      // focusRaw (texte brut). N8N reçoit ainsi le contexte du RDV/débrief
      // d'origine peu importe le chemin d'entrée.
      if (!coachingFocus && effectiveTarget?.visibility === "private") {
        const fromTarget = (effectiveTarget as Target & { scenarioInstruction?: string | null; focusRaw?: string | null }).scenarioInstruction
          || (effectiveTarget as Target & { scenarioInstruction?: string | null; focusRaw?: string | null }).focusRaw
          || undefined;
        if (fromTarget) coachingFocus = fromTarget;
      }

      const newScenario = await trainingApi.generateScenario(sessionForGen.id, {
        difficulty,
        personaId: persona?.id,
        ...(effectiveTarget?.id && { targetId: effectiveTarget.id }),
        ...(coachingFocus && { coachingFocus }),
        ...(coachingOfferId && { coachingOfferId }),
      });
      setScenario(newScenario);
      setPhase("ready");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("training.scenarioGenError");
      setError(message);
      setPhase("difficulty-select");
    }
  };

  const handleDifficultySelect = async (difficulty: string) => {
    if (!session) return;
    setSelectedDifficulty(difficulty);
    await runScenarioGeneration(session, difficulty, selectedPersona);
  };

  // Mock messages pour simuler une conversation
  const mockConversation = useCallback(() => {
    const prospectName = scenario?.prospectName || "le prospect";
    const mockMessages: Array<{ role: "user" | "agent"; content: string; delay: number }> = [
      { role: "agent", content: `Bonjour, ${prospectName} à l'appareil.`, delay: 1000 },
      { role: "user", content: "Bonjour, je suis commercial chez Kestio. Je vous contacte car nous aidons les entreprises à améliorer leurs performances commerciales.", delay: 3000 },
      { role: "agent", content: "Bonjour. Je n'ai pas beaucoup de temps, pouvez-vous aller à l'essentiel ?", delay: 5000 },
      { role: "user", content: "Bien sûr. Nous proposons des formations commerciales innovantes avec des simulations IA. Est-ce que la performance de vos équipes commerciales est un sujet pour vous ?", delay: 8000 },
      { role: "agent", content: "Oui, c'est effectivement un sujet. Nous avons du mal à closer nos deals.", delay: 11000 },
      { role: "user", content: "Je comprends. Beaucoup de nos clients avaient le même problème. Seriez-vous disponible pour un rendez-vous de 30 minutes la semaine prochaine pour que je vous présente notre approche ?", delay: 14000 },
      { role: "agent", content: "D'accord, je veux bien. Envoyez-moi une invitation pour mardi ou jeudi.", delay: 17000 },
    ];

    let messageIndex = 0;
    mockMessages.forEach((msg, index) => {
      setTimeout(() => {
        const newMessage: Message = {
          id: `mock-${index}`,
          sessionId: session?.id || "",
          content: msg.content,
          role: msg.role,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, newMessage]);
        messageIndex = index;
      }, msg.delay);
    });
  }, [scenario, session?.id]);

  const startCall = async () => {
    if (!session) return;
    endCallCalledRef.current = false;

    try {
      setError(null);
      setPhase("calling");
      callStartTimeSetRef.current = false;
      // Stay on scenario tab during call — user switches manually after call ends
      setIsMuted(true);

      if (MOCK_AUDIO_MODE) {
        // Mode mock : simuler la conversation avec des messages
        mockConversation();
      } else {
        // Mode réel : connexion via N8N + SDK @openai/agents
        const { agentConfig } = await trainingApi.getRealtimeToken(session.id, "simulator");
        // Mémorise la clé éphémère pour le relais sideband (déclenché par onCallId
        // une fois le call_id capturé côté hook). Best-effort.
        ephemeralKeyRef.current = agentConfig.client_secret?.value ?? null;
        await realtimeSession.connect(agentConfig);
        // Mic is physically muted inside connect() — no setMuted(true) here
        // so auto-unmute fires after agent's first greeting
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : t("training.startCallError");
      setError(message);
      setPhase("ready");
    }
  };

  // Génération du feedback + enchaînement coaching, isolée pour pouvoir être RELANCÉE
  // via le bouton « Réessayer » (cas d'un échec transitoire N8N) sans refaire la session.
  const runFeedback = async () => {
    if (!session) return;
    try {
      setError(null);
      setPhase("feedback-loading");
      setMainTab("coach");

      const newFeedback = await trainingApi.generateFeedback(session.id);
      setFeedback(newFeedback);

      // Auto-start coaching immediately after feedback is ready
      isCoachingRef.current = true;
      setPhase("coaching");
      setIsMuted(true);

      if (!MOCK_AUDIO_MODE) {
        const { agentConfig } = await trainingApi.getRealtimeToken(session.id, "coach");
        // Idem startCall : mémorise la clé éphémère pour le relais sideband.
        ephemeralKeyRef.current = agentConfig.client_secret?.value ?? null;
        await realtimeSession.connect(agentConfig);
        // Mic is physically muted inside connect() — auto-unmute after agent speaks
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t("training.endCallError");
      setError(message);
      setPhase("feedback");
    }
  };

  const endCall = async () => {
    if (!session || endCallCalledRef.current) return;
    endCallCalledRef.current = true;

    try {
      // Wait for agent to finish speaking + audio buffer, then disconnect
      if (!MOCK_AUDIO_MODE) {
        realtimeSession.setMuted(true);
        await realtimeSession.waitForSilence();
        // Capture coût audio AVANT disconnect (le snapshot d'usage doit être lu
        // pendant que le hook a encore les tokens accumulés). Best-effort.
        const usage = realtimeSession.getUsage();
        if (usage.inputTokens > 0 || usage.outputTokens > 0) {
          trainingApi.logSessionCost({
            sessionId: session.id,
            eventType: "audio_realtime",
            modelName: usage.model || "gpt-4o-realtime-preview",
            tokensInput: usage.inputTokens,
            tokensOutput: usage.outputTokens,
            tokensCached: usage.cachedTokens,
            audioInputTokens: usage.audioInputTokens,
            textInputTokens: usage.textInputTokens,
            audioOutputTokens: usage.audioOutputTokens,
            textOutputTokens: usage.textOutputTokens,
          });
        }
        realtimeSession.disconnect();
      }

      // Calculate duration
      const duration = callStartTime
        ? Math.max(1, Math.ceil((Date.now() - callStartTime.getTime()) / 60000))
        : 0;

      // Save messages (phase call)
      //
      // `rank` est l'index dans le tableau qu'on envoie, et il repart de zéro à chaque
      // phase : les deux phases sont deux conversations Realtime distinctes, chacune
      // sauvegardée à la fin de la sienne. Pas de compteur partagé à faire traverser —
      // un compteur ferait dépendre la justesse de l'ordre d'un détail invisible dans
      // les données (une ref remise à zéro, un remontage du composant), avec une
      // défaillance silencieuse à la clé.
      if (messages.length > 0) {
        await trainingApi.saveMessages(
          session.id,
          messages.map((m, rank) => ({ content: m.content, role: m.role, phase: "call", timestamp: m.timestamp, itemId: m.itemId, rank }))
        );
      }

      // Update session (duration + message count, endTime set at real end of session)
      await trainingApi.updateSession(session.id, {
        durationInMinutes: duration,
        numberOfMessages: messages.length,
      });

      // Génération du feedback (relançable via « Réessayer » — cf. runFeedback).
      await runFeedback();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("training.endCallError");
      setError(message);
      setPhase("feedback");
    }
  };

  // Keep ref in sync so onDisconnected can call endCall
  endCallRef.current = endCall;

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    realtimeSession.setMuted(newMuted);
  };

  const endCoaching = async () => {
    // Disconnect immediately — user explicitly ended the session
    if (!MOCK_AUDIO_MODE) {
      realtimeSession.setMuted(true);
      // Capture coût audio coaching avant disconnect (cumul depuis le début
      // de la phase coaching — l'accumulateur a été reset au connect coaching).
      const usage = realtimeSession.getUsage();
      if (session && (usage.inputTokens > 0 || usage.outputTokens > 0)) {
        trainingApi.logSessionCost({
          sessionId: session.id,
          eventType: "audio_realtime",
          modelName: usage.model || "gpt-4o-realtime-preview",
          tokensInput: usage.inputTokens,
          tokensOutput: usage.outputTokens,
          tokensCached: usage.cachedTokens,
          audioInputTokens: usage.audioInputTokens,
          textInputTokens: usage.textInputTokens,
          audioOutputTokens: usage.audioOutputTokens,
          textOutputTokens: usage.textOutputTokens,
          notes: "phase=coaching",
        });
      }
      realtimeSession.disconnect();
    }

    // Clear inactivity timers
    if (inactivityCheckRef.current) clearInterval(inactivityCheckRef.current);
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    if (goodbyeTimerRef.current) { clearTimeout(goodbyeTimerRef.current); goodbyeTimerRef.current = null; }
    setShowInactivityWarning(false);

    // Set endTime at the real end of session + sauvegarder les messages du coaching
    if (session) {
      try {
        // Sauvegarde des messages de la phase coaching (agent "coach" + user).
        // Ces messages sont dans un state React séparé et seraient perdus sinon.
        if (coachMessages.length > 0) {
          await trainingApi.saveMessages(
            session.id,
            coachMessages.map((m, rank) => ({ content: m.content, role: m.role, phase: "coaching", timestamp: m.timestamp, itemId: m.itemId, rank }))
          );
        }

        await trainingApi.updateSession(session.id, {
          endTime: new Date().toISOString(),
        });
      } catch {
        console.error("Erreur lors de la mise à jour de endTime ou sauvegarde coachMessages");
      }
    }

    setPhase("done");
    // Ne pas rediriger — l'utilisateur reste sur la page pour consulter ses résultats
  };

  // Keep ref in sync for inactivity timer callback
  endCoachingRef.current = endCoaching;

  const resetSession = async () => {
    if (!MOCK_AUDIO_MODE) {
      realtimeSession.disconnect();
    }

    // Signal session no longer active
    onSessionActive?.(false);

    // Set endTime on current session before resetting
    if (session) {
      try {
        await trainingApi.updateSession(session.id, {
          endTime: new Date().toISOString(),
        });
      } catch {
        console.error("Erreur lors de la mise à jour de endTime");
      }
    }

    // « Rejouer » d'une simulation issue d'un contexte coaching (Prep Meeting /
    // Coach Debrief) : on re-pose le contexte consommé avec un createdAt frais, pour
    // que initializeSession relance directement le MÊME cas (auto-skip) au lieu
    // d'afficher la liste des cas. Session classique → ref null → comportement inchangé.
    if (lastCoachingContextRef.current) {
      try {
        localStorage.setItem(
          "bizcoach_pending_coaching_context",
          JSON.stringify({ ...lastCoachingContextRef.current, createdAt: Date.now() }),
        );
      } catch { /* noop */ }
    }

    // Idem pour une phase de parcours certifiant : sans ça, « Rejouer » perdait le
    // contexte et proposait la liste libre des cas d'équipe (bug Mariam 08/07/26).
    if (lastPathPhaseRef.current) {
      try {
        localStorage.setItem(
          "bizcoach_pending_path_phase",
          JSON.stringify({ ...lastPathPhaseRef.current, createdAt: Date.now() }),
        );
      } catch { /* noop */ }
    }

    isCoachingRef.current = false;
    endCallCalledRef.current = false;
    setSession(null);
    setScenario(null);
    setPhase("init");
    setMessages([]);
    setFeedback(null);
    setCoachMessages([]);
    setMainTab("prospect");
    setSubTab("scenario");
    setCoachSubTab("feedback");
    setIsMuted(true);
    setCallStartTime(null);
    callStartTimeSetRef.current = false;
    setError(null);
    setAllTargets([]);
    setSelectedTarget(null);
    setSelectedPersona(null);
    setSelectedDifficulty(null);
    setRelanceCard(null);
    setShowReplayFallback(false);
    setRetrainFocus(null);
    setFocusDelta(null);
    setDuelContext(null);

    // Re-initialize
    initializeSession();
  };

  // --- Relance intelligente (Étape 5) ---

  // Relance le MÊME cas (offre + persona) à une difficulté donnée, en repartant
  // directement sur la génération de scénario (skip la sélection).
  const relaunchSameCase = async (
    difficulty: string,
    focus?: { focusCriterionKey: string; sourceSessionId: string },
  ) => {
    if (!MOCK_AUDIO_MODE) realtimeSession.disconnect();
    onSessionActive?.(false);
    if (session) {
      try {
        await trainingApi.updateSession(session.id, { endTime: new Date().toISOString() });
      } catch {
        /* noop */
      }
    }
    const target = selectedTarget;
    const persona = selectedPersona;
    if (!target) {
      resetSession();
      return;
    }
    isCoachingRef.current = false;
    endCallCalledRef.current = false;
    setScenario(null);
    setMessages([]);
    setFeedback(null);
    setCoachMessages([]);
    setMainTab("prospect");
    setSubTab("scenario");
    setCoachSubTab("feedback");
    setIsMuted(true);
    setCallStartTime(null);
    callStartTimeSetRef.current = false;
    setError(null);
    setRelanceCard(null);
    setShowReplayFallback(false);
    setFocusDelta(null);
    setDuelContext(null);
    // Focus de re-train : effacé si la relance ne cible pas un critère (relance
    // « rejouer » / « difficulté sup »), rechargé plus bas si un focus est passé.
    if (!focus) setRetrainFocus(null);
    setPhase("init");
    try {
      const newSession = await trainingApi.createSession(agent.id, challengeId, undefined, focus);
      setSession(newSession);
      setSelectedTarget(target);
      setSelectedPersona(persona);
      setSelectedDifficulty(difficulty);
      // Re-train ciblé (Étape 6) : récupère le libellé bilingue du critère travaillé
      // pour le bandeau « focus verrouillé » affiché pendant la séance.
      if (focus) {
        relanceApi
          .getFocus(newSession.id)
          .then((r) => setRetrainFocus(r.focus))
          .catch(() => setRetrainFocus(null));
      }
      await runScenarioGeneration(newSession, difficulty, persona, target);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("training.scenarioGenError"));
      setPhase("difficulty-select");
    }
  };

  // Exécute l'action d'un axe. Les axes « en place » relancent le cas ; les axes
  // qui changent de simulateur/contexte quittent la session (routage approfondi
  // en Étapes 6/7).
  const executeRelanceAxis = async (axis: string) => {
    if (axis === "difficulty_up") {
      await relaunchSameCase(relanceCard?.nextDifficulty || "hard");
      return;
    }
    if (axis === "criterion_retrain") {
      // Re-train ciblé (Étape 6) : rejoue le MÊME cas à difficulté héritée, focus
      // verrouillé sur le critère faible, rattaché au débrief source (pour le delta).
      const focus =
        relanceCard?.weakestCriterionKey && session?.id
          ? { focusCriterionKey: relanceCard.weakestCriterionKey, sourceSessionId: session.id }
          : undefined;
      await relaunchSameCase(selectedDifficulty || relanceCard?.difficulty || "medium", focus);
      return;
    }
    if (axis === "retry_same") {
      await relaunchSameCase(selectedDifficulty || relanceCard?.difficulty || "medium");
      return;
    }
    onBack(); // next_phase / offer_variation / real_meeting_bridge / prep_meeting
  };

  const handleRelance = async (axis: string) => {
    if (!relanceCard) return;
    setRelanceBusy(true);
    try {
      await relanceApi.act(relanceCard.eventId, axis);
    } catch {
      /* noop */
    }
    await executeRelanceAxis(axis);
    setRelanceBusy(false);
  };

  const handleRelanceDismiss = async () => {
    if (relanceCard) {
      try {
        await relanceApi.act(relanceCard.eventId, "dismiss");
      } catch {
        /* noop */
      }
    }
    setShowReplayFallback(true);
  };

  // Récupère la carte de relance à l'affichage du bilan (hors challenge).
  useEffect(() => {
    if (phase === "feedback" && !challengeId && session?.id && !relanceCard) {
      relanceApi
        .getForSession(session.id)
        .then((c) => setRelanceCard(c))
        .catch(() => {
          /* silencieux : on retombe sur le bouton Rejouer classique */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session, challengeId]);

  // Re-train ciblé (Étape 6) : au bilan d'une séance de re-train, récupère le
  // delta du critère travaillé (avant → après) pour le chip de progression.
  useEffect(() => {
    if (phase === "feedback" && session?.id) {
      relanceApi
        .getFocus(session.id)
        .then((r) => {
          setRetrainFocus(r.focus);
          setFocusDelta(r.delta);
        })
        .catch(() => {
          /* silencieux : pas de focus sur cette session */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session]);

  // Rendu : chip de delta (si séance de re-train) puis carte de relance (ou
  // bouton « Rejouer » classique).
  const renderPostSession = () => {
    if (challengeId) return null;
    // Re-train ciblé (Étape 6) : progression du critère travaillé, avant → après.
    const deltaChip = focusDelta ? <FocusDeltaChip focus={retrainFocus} delta={focusDelta} lang={lang} /> : null;
    if (relanceCard && !showReplayFallback) {
      return (
        <div className="flex flex-col items-center">
          {deltaChip}
          <RelanceCard
            card={relanceCard}
            lang={lang}
            busy={relanceBusy}
            onPrimary={() => handleRelance(relanceCard.primary.axis)}
            onSecondary={() => relanceCard.secondary && handleRelance(relanceCard.secondary.axis)}
            onDismiss={handleRelanceDismiss}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center">
        {deltaChip}
        <Button
          size="default"
          className="px-4 sm:px-8 text-sm sm:text-base bg-green-600 hover:bg-green-700 text-white"
          onClick={resetSession}
        >
          <RotateCcw className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
          {t("training.replay")}
        </Button>
      </div>
    );
  };

  // --- Inactivity detection during coaching ---
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showInactivityWarning) {
      setShowInactivityWarning(false);
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    }
  }, [showInactivityWarning]);

  // Track activity from agent speaking and new messages (coaching ET calling)
  useEffect(() => {
    if (phaseRef.current === "coaching" || phaseRef.current === "calling") {
      lastActivityRef.current = Date.now();
    }
  }, [realtimeSession.isAgentSpeaking, coachMessages.length, messages.length, isMuted]);

  // Inactivity check interval during coaching
  useEffect(() => {
    if (phase === "coaching") {
      lastActivityRef.current = Date.now();
      inactivityCheckRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= INACTIVITY_TIMEOUT && !showInactivityWarning) {
          setShowInactivityWarning(true);
          autoCloseRef.current = setTimeout(() => {
            endCoachingRef.current();
          }, AUTO_CLOSE_DELAY);
        }
      }, 30000); // check every 30s

      return () => {
        if (inactivityCheckRef.current) clearInterval(inactivityCheckRef.current);
        if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
      };
    }
  }, [phase]);

  // Filet phase APPEL nº1 — silence TOTAL prolongé (session abandonnée).
  // Depuis le durcissement de hangUp (le simulateur ne raccroche plus de sa propre
  // initiative) et EN L'ABSENCE de tout timer de durée max en calling, une session
  // « le commercial est parti sans raccrocher » tournerait indéfiniment (coût qui file).
  // Après CALLING_INACTIVITY_TIMEOUT (90 s) de silence total (lastActivityRef non réarmé
  // par la parole) → auto-fin directe via endCall. Seuil > 30 s pour ne pas couper une
  // simple pause de réflexion en plein échange.
  useEffect(() => {
    if (phase === "calling") {
      lastActivityRef.current = Date.now();
      inactivityCheckRef.current = setInterval(() => {
        if (Date.now() - lastActivityRef.current >= CALLING_INACTIVITY_TIMEOUT) {
          realtimeSession.setEndReason("calling_inactivity"); // diagnostic : distinguer l'auto-fin d'abandon
          endCallRef.current();
        }
      }, 10000); // check toutes les 10 s

      return () => {
        if (inactivityCheckRef.current) clearInterval(inactivityCheckRef.current);
      };
    }
  }, [phase]);

  // Filet phase APPEL nº2 — politesses de fin détectées mais le modèle n'a pas raccroché.
  // On scanne la transcription : dès qu'une formule de clôture apparaît (« au revoir »,
  // « bonne journée »…), on arme un chrono GOODBYE_HANGUP_DELAY (30 s). Si un nouvel échange
  // NON-clôture arrive avant, la conversation reprend → on annule. Sinon → auto-fin via endCall.
  useEffect(() => {
    if (phaseRef.current !== "calling") return;
    const last = messages[messages.length - 1];
    if (!last?.content) return;
    // Formules de clôture FR (le simulateur parle français). Insensible à la casse/accents partiels.
    const CLOSING = /\b(au revoir|bonne journ[ée]e|bonne fin de journ[ée]e|bonne soir[ée]e|belle journ[ée]e|à bient[ôo]t|au plaisir|bonne continuation|je vous laisse|je ne vous d[ée]range pas plus|passez une (bonne|belle))\b/i;
    if (CLOSING.test(last.content)) {
      if (goodbyeTimerRef.current) clearTimeout(goodbyeTimerRef.current);
      goodbyeTimerRef.current = setTimeout(() => {
        realtimeSession.setEndReason("goodbye_timeout"); // diagnostic : distinguer l'auto-fin post-politesses
        endCallRef.current();
      }, GOODBYE_HANGUP_DELAY);
    } else if (goodbyeTimerRef.current) {
      // Échange substantiel après les politesses → la conversation continue, on annule.
      clearTimeout(goodbyeTimerRef.current);
      goodbyeTimerRef.current = null;
    }
  }, [messages]);

  // Le warning d'inactivité ne doit jamais survivre à un changement de phase
  // (ex. auto-fin d'appel → écran feedback : le dialog ne doit pas rester affiché).
  useEffect(() => {
    setShowInactivityWarning(false);
  }, [phase]);

  // Warn before leaving during active session
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (phaseRef.current === "calling" || phaseRef.current === "coaching") {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const getScoreColor = (score: number, max = 10) => {
    const pct = score / max;
    if (pct >= 0.7) return { text: "text-kestio-green", bg: "bg-kestio-green" };
    if (pct >= 0.4) return { text: "text-kestio-yellow", bg: "bg-kestio-yellow" };
    return { text: "text-kestio-coral", bg: "bg-kestio-coral" };
  };

  const overallKeys = ["overall", "global"];
  // N'affiche que les critères réellement évalués (présents dans feedback.scores)
  // Les critères non sélectionnés ne doivent pas apparaître à 0/10
  const evaluatedScores = feedback?.scores;
  const isEvaluatedKey = (key: string) =>
    !evaluatedScores || Object.prototype.hasOwnProperty.call(evaluatedScores, key);
  const scoringCriteria = (agent.scoringCriteria || []).filter(c => c.type === "score" && isEvaluatedKey(c.key));
  const scoringCriteriaForTotal = scoringCriteria.filter(c => !overallKeys.includes(c.key));
  const booleanCriteria = (agent.scoringCriteria || []).filter(c => c.type === "boolean" && isEvaluatedKey(c.key));

  // « S'exercer » : icône sur les 3 critères à plus forte marge de progression AYANT une fiche active.
  // Étoile sur le critère conseillé par le coach (relanceCard.weakestCriterionKey). Offert (origin=feedback).
  const [drillEligibleKeys, setDrillEligibleKeys] = useState<Set<string>>(new Set());
  // Seconde protection de l'écran (13/10/2026) : S'exercer fermé → aucune icône, même si le serveur en servait une.
  const drillOuvert = useDrillVisible();
  useEffect(() => {
    if (!drillOuvert || !feedback || !agent?.id || !onNavigate) { setDrillEligibleKeys(new Set()); return; }
    let cancelled = false;
    drillSessionApi.eligibleCriteria(agent.id)
      .then((rows) => { if (!cancelled) setDrillEligibleKeys(new Set(rows.map((r) => r.criterionKey))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [drillOuvert, feedback, agent?.id, onNavigate]);
  const drillableKeys = lowest3Drillable(
    scoringCriteriaForTotal.map((c) => ({ key: c.key, score: Number(feedback?.scores?.[c.key]) || 0 })),
    drillEligibleKeys,
  );
  const launchDrillFromFeedback = (c: { key: string; label?: string; labelFr?: string; labelEn?: string }) => {
    if (!onNavigate) return;
    launchDrill({
      criterionKey: c.key,
      criterionLabel: getLabel(c),
      simulatorId: agent.id,
      simulatorLabel: agent.name,
      origin: "feedback",
      sourceSessionId: session?.id ?? undefined,
    }, onNavigate);
  };

  const isLoading = phase === "init" || phase === "scenario-loading" || phase === "feedback-loading";
  const isTargetSelect = phase === "target-select";
  const isDifficultySelect = phase === "difficulty-select";
  const callStarted = phase === "calling" || phase === "feedback-loading" || phase === "feedback" || phase === "coaching" || phase === "done";
  const callEnded = phase === "feedback-loading" || phase === "feedback" || phase === "coaching" || phase === "done";
  const coachingEnded = phase === "done";

  return (
    <>
    {/* Cadre « DUEL » brillant plein écran (Étape 8) — pendant toute la passe. */}
    {duelContext && <DuelFrame opponentName={duelContext.opponentName} stakeCoins={duelContext.stakeCoins} />}
    <Dialog open={!!quotaReached} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>{t("training.simulationQuotaReachedTitle")}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {quotaReached && t("training.simulationQuotaReachedMessage", { used: quotaReached.used, max: quotaReached.max })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onBack}>
            {t("training.simulationQuotaReachedBack")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <SessionQuotaBlockedDialog
      info={sessionQuotaBlocked}
      onClose={() => { setSessionQuotaBlocked(null); onBack(); }}
      onCta={(url) => { setSessionQuotaBlocked(null); window.location.href = url; }}
    />
    {/* Phase A 2026-05-14 — Dialog erreur audio Realtime (mic refusé, connexion down).
        Avant, le user parlait à vide sans aucun feedback. */}
    <Dialog open={!!realtimeError} onOpenChange={(open) => { if (!open) setRealtimeError(null); }}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <ShieldAlert className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle>{t("training.audioErrorTitle")}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {realtimeError}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setRealtimeError(null)}>{t("training.audioErrorClose")}</Button>
          <Button onClick={() => { setRealtimeError(null); window.location.reload(); }}>
            {t("training.audioErrorReload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <div className="flex flex-col h-[calc(100dvh-5.5rem)] lg:h-[calc(100dvh-7rem)]">
      {/* Célébration plein écran à la réussite d'une phase */}
      <Celebration
        show={celebrate}
        title={pathContext ? t("certPaths.celebrateTitle") : t("challenge.celebrateTitle")}
        subtitle={pathContext
          ? t("certPaths.celebrateSubtitle", { title: pathContext.phaseTitle })
          : t("challenge.celebrateSubtitle", { title: challengeTitle })}
        closeLabel={t("common.continue")}
        onClose={() => setCelebrate(false)}
      />

      {/* Bandeau distinctif : session menée dans le cadre d'un parcours / challenge */}
      {(pathContext || challengeId) && (
        <div
          className="mb-2 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm"
          style={pathContext
            ? { background: "linear-gradient(90deg,#988EF222,#A6D3F922)", border: "1px solid #988EF2" }
            : { background: "#F6C84722", border: "1px solid #F6C847" }}
        >
          {pathContext ? (
            <>
              <Award className="h-4 w-4 shrink-0" style={{ color: "#5A4BBE" }} />
              <span className="font-semibold" style={{ color: "#5A4BBE" }}>{pathContext.pathTitle}</span>
              <span className="text-muted-foreground">·</span>
              <span className="font-medium" style={{ color: "#384872" }}>
                {t("certPaths.bannerPhase", { n: pathContext.sequence, total: pathContext.totalPhases })} — {pathContext.phaseTitle}
              </span>
              {pathContext.primaryObjectiveLabel && (
                <span className="ml-auto rounded-full px-2 py-0.5 text-xs text-white" style={{ background: "#988EF2" }}>
                  🎯 {pathContext.primaryObjectiveLabel}{pathContext.primaryObjectiveTarget != null ? ` ≥ ${pathContext.primaryObjectiveTarget}` : ""}
                  {pathContext.secondaryObjectiveLabel ? ` · +${pathContext.secondaryObjectiveLabel}` : ""}
                </span>
              )}
            </>
          ) : (
            <>
              <Trophy className="h-4 w-4 shrink-0" style={{ color: "#926B00" }} />
              <span className="font-semibold" style={{ color: "#926B00" }}>{t("certPaths.bannerChallenge")}</span>
              {challengeTitle && <span className="font-medium" style={{ color: "#384872" }}>· {challengeTitle}</span>}
              {challengeProgress && (
                <span className="ml-auto flex items-center gap-2">
                  {challengeProgress.primaryObjective && (
                    <span className="rounded-full px-2 py-0.5 text-xs text-white" style={{ background: "#926B00" }}>
                      🎯 {objectiveLabel(challengeProgress.primaryObjective, agent, t, lang)}
                    </span>
                  )}
                  <span className="text-xs font-medium" style={{ color: "#384872" }}>
                    {t("certPaths.progressSessions", { done: challengeProgress.sessionsCount, min: challengeProgress.minSessions })}
                  </span>
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Re-train ciblé (Étape 6) : bandeau « focus verrouillé » pendant la séance. */}
      {retrainFocus && phase !== "feedback" && (
        <RetrainFocusBanner focus={retrainFocus} lang={lang} />
      )}

      {/* Encart d'aide micro — affiché quand getUserMedia a réussi MAIS que le
          système ne fournit aucun son (track.muted), ou micro absent/refusé.
          Cas typique : MAJ macOS (Tahoe) qui révoque l'autorisation micro de
          Chrome au niveau OS → l'utilisateur parle dans le vide sans erreur.
          Non bloquant : l'utilisateur peut le fermer. */}
      {realtimeSession.micIssue && (
        <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" role="alert">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <MicOff className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">{t("training.micHelpTitle")}</p>
              <p className="mt-0.5 text-amber-800">
                {realtimeSession.micIssue === "denied"
                  ? t("training.micHelpDenied")
                  : realtimeSession.micIssue === "no-device"
                    ? t("training.micHelpNoDevice")
                    : t("training.micHelpSilent")}
              </p>
              <ol className="mt-2 list-decimal space-y-0.5 pl-4 text-amber-800">
                <li>{t("training.micHelpStepBrowser")}</li>
                <li>{t("training.micHelpStepMacOS")}</li>
                <li>{t("training.micHelpStepRestart")}</li>
                <li>{t("training.micHelpStepDevice")}</li>
              </ol>
            </div>
            <button
              type="button"
              onClick={() => realtimeSession.clearMicIssue()}
              className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
            >
              {t("training.micHelpDismiss")}
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {agent.image ? (
            <img src={agent.image} alt={agent.name} loading="lazy" className="h-7 w-7 rounded-full object-cover" style={{ objectPosition: 'top' }} />
          ) : (
            <span className="text-lg">{agent.icon}</span>
          )}
          <h1 className="text-base font-bold">{agent.name}</h1>
          <p className="text-xs text-muted-foreground hidden sm:block truncate">— {pickSimulatorDescription(agent, i18n.language)}</p>
        </div>
        <div className="flex items-center justify-center flex-1">
          {/* Kitchen-style timer — visible during calling phase */}
          {phase === "calling" && callStartTime && challengeId && (orangeThreshold || redThreshold) && (() => {
            const mins = elapsedSeconds / 60;
            const isRed = !!(redThreshold && mins >= redThreshold);
            const isOrange = !!(orangeThreshold && mins >= orangeThreshold);
            const displayMins = Math.floor(elapsedSeconds / 60);
            const displaySecs = elapsedSeconds % 60;

            const timeColor = isRed ? "#ef4444" : isOrange ? "#f59e0b" : "#22c55e";
            const maxMins = redThreshold ? redThreshold * 1.2 : orangeThreshold ? orangeThreshold * 2 : 30;

            // SVG params
            const size = 88;
            const cx = size / 2;
            const cy = size / 2;
            const r = 36;
            const circ = 2 * Math.PI * r;

            // Angle helper: 0min = top (12 o'clock), clockwise
            const minToAngle = (m: number) => (m / maxMins) * 360 - 90;
            const minToRad = (m: number) => (minToAngle(m) * Math.PI) / 180;

            // Zone arcs (green → orange → red)
            const orangeStart = orangeThreshold || maxMins;
            const redStart = redThreshold || maxMins;

            const arcPath = (startMin: number, endMin: number) => {
              const a1 = minToRad(startMin);
              const a2 = minToRad(endMin);
              const x1 = cx + r * Math.cos(a1);
              const y1 = cy + r * Math.sin(a1);
              const x2 = cx + r * Math.cos(a2);
              const y2 = cy + r * Math.sin(a2);
              const large = (endMin - startMin) / maxMins > 0.5 ? 1 : 0;
              return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
            };

            // Needle position
            const needleAngle = minToRad(Math.min(mins, maxMins));
            const needleLen = r - 6;
            const nx = cx + needleLen * Math.cos(needleAngle);
            const ny = cy + needleLen * Math.sin(needleAngle);

            // Threshold tick marks
            const tickMark = (m: number, color: string) => {
              const a = minToRad(m);
              const outerR = r + 2;
              const innerR = r - 5;
              return (
                <line
                  key={m}
                  x1={cx + innerR * Math.cos(a)} y1={cy + innerR * Math.sin(a)}
                  x2={cx + outerR * Math.cos(a)} y2={cy + outerR * Math.sin(a)}
                  stroke={color} strokeWidth="2.5" strokeLinecap="round"
                />
              );
            };

            // Threshold label positions (outside ring)
            const thresholdLabel = (m: number, color: string, label: string) => {
              const a = minToRad(m);
              const lr = r + 11;
              return (
                <text
                  key={`label-${m}`}
                  x={cx + lr * Math.cos(a)} y={cy + lr * Math.sin(a)}
                  fill={color} fontSize="8" fontWeight="700" textAnchor="middle" dominantBaseline="central"
                >
                  {label}
                </text>
              );
            };

            return (
              <div className="flex items-center">
                <div className="relative" style={{ width: size, height: size }}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {/* Background circle */}
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />

                    {/* Zone arcs */}
                    <path d={arcPath(0, Math.min(orangeStart, maxMins))} fill="none" stroke="#86efac" strokeWidth="6" strokeLinecap="round" />
                    {orangeThreshold && orangeThreshold < maxMins && (
                      <path d={arcPath(orangeStart, Math.min(redStart, maxMins))} fill="none" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round" />
                    )}
                    {redThreshold && redThreshold < maxMins && (
                      <path d={arcPath(redStart, maxMins)} fill="none" stroke="#fca5a5" strokeWidth="6" strokeLinecap="round" />
                    )}

                    {/* Threshold tick marks */}
                    {orangeThreshold && tickMark(orangeThreshold, "#f59e0b")}
                    {redThreshold && tickMark(redThreshold, "#ef4444")}

                    {/* Threshold labels */}
                    {orangeThreshold && thresholdLabel(orangeThreshold, "#f59e0b", `${orangeThreshold}'`)}
                    {redThreshold && thresholdLabel(redThreshold, "#ef4444", `${redThreshold}'`)}

                    {/* Center dot */}
                    <circle cx={cx} cy={cy} r="3" fill={timeColor} />

                    {/* Needle */}
                    <line
                      x1={cx} y1={cy} x2={nx} y2={ny}
                      stroke={timeColor} strokeWidth="2.5" strokeLinecap="round"
                      style={{ transition: "x2 1s linear, y2 1s linear, stroke 0.5s ease" }}
                    />
                  </svg>
                  {/* Center: time display */}
                  <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: 20 }}>
                    <span className="font-mono text-base font-extrabold tracking-tight" style={{ color: timeColor, transition: "color 0.5s ease" }}>
                      {String(displayMins).padStart(2, "0")}:{String(displaySecs).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 flex-1 justify-end">
          {feedback && (
            <>
              <span className="text-lg font-bold text-foreground">
                {feedback.totalScore}/{scoringCriteriaForTotal.length * 10}
              </span>
              {booleanCriteria.map(c => {
                const val = feedback.scores?.[c.key];
                return val ? (
                  <Badge key={c.key} variant="default" className="text-xs px-2 py-0">
                    {getLabel(c)}
                  </Badge>
                ) : null;
              })}
              {scoringCriteria.filter(c => overallKeys.includes(c.key) && c.successThreshold != null).map(c => {
                const val = Number(feedback.scores?.[c.key]) || 0;
                const passed = val >= c.successThreshold!;
                const label = passed
                  ? pickVerdictLabel(c, lang, true) || t("training.verdictPassed")
                  : pickVerdictLabel(c, lang, false) || t("training.verdictFailed");
                return (
                  <Badge key={c.key} className={`text-xs px-2 py-0 ${passed ? "bg-kestio-green text-white" : "bg-kestio-coral text-white"}`}>
                    {passed ? "✓" : "✗"} {label}
                  </Badge>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Mock Mode Banner */}
      {MOCK_AUDIO_MODE && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-700 rounded-md px-4 py-2 mb-2 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          <span>{t("training.demoMode").replace(/<[^>]*>/g, "")}</span>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-4 py-2 mb-2 text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          {phase === "feedback" && (
            <button
              type="button"
              onClick={runFeedback}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-1 text-xs font-medium hover:bg-destructive/10"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t("training.retryFeedback")}
            </button>
          )}
        </div>
      )}

      {/* Parcours — état de progression de la phase après le débrief */}
      {pathContext && feedback && phaseResult?.enrolled && (
        <div
          className="mb-2 rounded-lg border p-3"
          style={phaseResult.status === "done"
            ? { borderColor: "#7CBF94", background: "#7CBF9418" }
            : { borderColor: "#A6D3F9", background: "#A6D3F918" }}
        >
          <div className="mb-1 flex items-center gap-2">
            <Award className="h-4 w-4" style={{ color: "#5A4BBE" }} />
            <span className="text-sm font-semibold" style={{ color: "#384872" }}>
              {phaseResult.status === "done" ? t("certPaths.phaseDoneTitle") : t("certPaths.phaseProgressTitle")}
            </span>
          </div>
          <ul className="space-y-0.5 text-xs">
            <li className={phaseResult.sessionsRemaining === 0 ? "text-green-700" : "text-muted-foreground"}>
              {phaseResult.sessionsRemaining === 0 ? "✓ " : "• "}
              {t("certPaths.progressSessions", { done: phaseResult.sessionsCount, min: phaseResult.minSessions })}
            </li>
            <li className={phaseResult.primaryAchieved ? "text-green-700" : "text-muted-foreground"}>
              {phaseResult.primaryAchieved ? "✓ " : "• "}
              {t("certPaths.progressObjective")}
            </li>
          </ul>
          {phaseResult.status !== "done" && (
            <p className="mt-1.5 text-xs font-medium" style={{ color: "#5A4BBE" }}>{t("certPaths.phaseRemainingHint")}</p>
          )}
        </div>
      )}

      {/* Challenge — état de progression après le débrief */}
      {challengeId && feedback && challengeProgress && (
        <div
          className="mb-2 rounded-lg border p-3"
          style={challengeProgress.sessionsCount >= challengeProgress.minSessions
            ? { borderColor: "#7CBF94", background: "#7CBF9418" }
            : { borderColor: "#F6C847", background: "#F6C84720" }}
        >
          <div className="mb-1 flex items-center gap-2">
            <Trophy className="h-4 w-4" style={{ color: "#926B00" }} />
            <span className="text-sm font-semibold" style={{ color: "#384872" }}>
              {challengeProgress.sessionsCount >= challengeProgress.minSessions ? t("challenge.completeTitle") : t("challenge.progressTitle")}
            </span>
          </div>
          <p className="text-xs" style={{ color: challengeProgress.sessionsCount >= challengeProgress.minSessions ? "#1E7A3C" : "#6B7280" }}>
            {challengeProgress.sessionsCount >= challengeProgress.minSessions ? "✓ " : "• "}
            {t("certPaths.progressSessions", { done: challengeProgress.sessionsCount, min: challengeProgress.minSessions })}
          </p>
        </div>
      )}

      {/* Main Content — `gap-0 pt-2` : le Card ajoute par défaut 24 px au-dessus des onglets
          et 24 px entre chaque rangée ; sur le choix du cas, les onglets des cas sortaient
          de l'écran (demande de Valérie du 23/10/2026). L'écart avant le contenu des autres
          étapes est rendu par le `pt-6` du CardContent. */}
      <Card className={`flex-1 flex flex-col min-h-0 gap-0 pt-2 ${isTargetSelect ? "pb-2" : ""}`}>
        {/* Primary Tabs */}
        <div className="border-b px-4 pt-0">
          <div className="flex gap-1">
            <button
              onClick={() => setMainTab("prospect")}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                mainTab === "prospect"
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              style={mainTab === "prospect" ? { backgroundColor: agentColor } : undefined}
            >
              <Phone className="h-4 w-4" />
              {t("training.prospect")}
            </button>
            {callEnded && (
              <button
                onClick={() => setMainTab("coach")}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                  mainTab === "coach"
                    ? "text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
                style={mainTab === "coach" ? { backgroundColor: agentColor } : undefined}
              >
                <GraduationCap className="h-4 w-4" />
                {t("training.salesCoach")}
              </button>
            )}
          </div>
        </div>

        {/* Sub-tabs */}
        {mainTab === "prospect" && (
          <div className="border-b px-4 pt-0">
            <div className="flex gap-1">
              <button
                onClick={() => setSubTab("scenario")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-t-md text-sm transition-colors ${
                  subTab === "scenario"
                    ? "border-b-2 font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                style={subTab === "scenario" ? { borderColor: agentColor, color: agentColor } : undefined}
              >
                <FileText className="h-3.5 w-3.5" />
                {t("training.scenario")}
              </button>
              {callEnded && (
                <button
                  onClick={() => setSubTab("call")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-t-md text-sm transition-colors ${
                    subTab === "call"
                      ? "border-b-2 font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={subTab === "call" ? { borderColor: agentColor, color: agentColor } : undefined}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {t("training.call")}
                </button>
              )}
            </div>
          </div>
        )}
        {mainTab === "coach" && callEnded && (
          <div className="border-b px-4 pt-0">
            <div className="flex gap-1">
              <button
                onClick={() => setCoachSubTab("feedback")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-t-md text-sm transition-colors ${
                  coachSubTab === "feedback"
                    ? "border-b-2 font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                style={coachSubTab === "feedback" ? { borderColor: agentColor, color: agentColor } : undefined}
              >
                <FileText className="h-3.5 w-3.5" />
                Feedback
              </button>
              {coachingEnded && (
                <button
                  onClick={() => setCoachSubTab("exchanges")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-t-md text-sm transition-colors ${
                    coachSubTab === "exchanges"
                      ? "border-b-2 font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={coachSubTab === "exchanges" ? { borderColor: agentColor, color: agentColor } : undefined}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Coaching
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <CardContent className={`flex-1 ${isTargetSelect ? "pt-0" : "pt-6"} min-h-0 ${mainTab === "coach" && feedback ? "flex flex-col overflow-hidden" : "overflow-auto"}`}>
          {/* Loading state — Phase A 2026-05-14 : ETA visible pour rassurer
              le user pendant les attentes N8N (10-30s) */}
          {(phase === "init" || phase === "scenario-loading") && (
            <div className="flex flex-col items-center justify-center h-full px-4 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="font-semibold mb-1" style={{ color: "#384872" }}>
                {phase === "init" ? t("training.initSession") : t("training.generatingScenario")}
              </p>
              {phase === "scenario-loading" && (
                <>
                  <p className="text-sm text-muted-foreground max-w-md mb-3">
                    {t("training.scenarioLoadingDescription")}
                  </p>
                  <p className="text-xs italic" style={{ color: "#9CA3AF" }}>
                    {t("training.scenarioLoadingTip")}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Target + Persona selection (2-step progressive disclosure) */}
          {isTargetSelect && (
            <div className="flex flex-col items-center h-full px-4 pt-3 pb-4 overflow-y-auto">
              {allTargets.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1">
                  <p className="text-muted-foreground text-sm text-center max-w-md">
                    {t("account.noPersonaeForSimulator")}
                  </p>
                </div>
              ) : targetSelectStep === "target" ? (
                /* Step 1: Choose target — MIX :
                   - Layout grid 2 colonnes + cartes enrichies (refonte UX 06/05)
                   - Sections "Mes cas personnels" / "Cas de mon équipe" + différenciation
                     visuelle des cas privés (Sales Coach Réel V3, 15/05)
                   - Bandeau critères d'évaluation du simulateur en haut */
                /* Toute la largeur et un titre sur une ligne : les onglets des cas doivent
                   se voir sans faire défiler (demande de Valérie du 23/10/2026). */
                <div className="w-full">
                  <div className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
                    <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: agentColor + "15" }}>
                      <Building2 className="h-4 w-4" style={{ color: agentColor }} />
                    </div>
                    <h3 className="font-semibold text-lg">{t("account.chooseTargetPersona")}</h3>
                    <p className="text-muted-foreground text-sm">{t("account.selectPersona")}</p>
                  </div>

                  {/* Bandeau critères d'évaluation du simulateur */}
                  {evaluationCriteria.length > 0 && (
                    <div className="mb-3 flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2 text-center">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        {t("training.evaluationCriteria")} ({evaluationCriteria.length})
                      </p>
                      <p className="text-xs text-slate-700 leading-relaxed">
                        {evaluationCriteria.map((c: any) => pickScoringLabel(c, lang)).filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  )}

                  {(() => {
                    const isFr = i18n.language === "fr";
                    // Banque de cas Kestio : des cas prêts à jouer, communs à tous les comptes.
                    // Ils ne sont ni « d'équipe » ni « personnels » — les mélanger aux cas du
                    // client laisserait croire que quelqu'un les a écrits chez lui.
                    const isSocle = (t: any) => t.kind === "socle";
                    const privateTargets = allTargets.filter((t) => t.visibility === "private" && !isSocle(t));
                    const publicTargets = allTargets.filter((t) => t.visibility !== "private" && !isSocle(t));

                    // Recommandation : on met en avant UN cas, pas une liste (cf. spec §4).
                    // Trois signaux disponibles sans appel supplémentaire — jamais joué d'abord
                    // (l'entraînement vaut par la variété), puis le simulateur natif, puis le
                    // plus accessible. Le point faible de l'apprenant affinera ce tri quand le
                    // graphe de compétences sera lu ici.
                    const DIFF_ORDER: Record<string, number> = { easy: 0, medium: 1, hard: 2 };

                    // Marché du compte. Depuis le 27/08, l'entreprise DÉCLARE ses modèles de vente
                    // et le serveur filtre la banque en conséquence — c'est la source de vérité.
                    // Ce filtre-ci ne sert que de repli, pour un compte qui n'a encore rien
                    // déclaré : on déduit alors des cas qu'il a lui-même écrits, plutôt que de
                    // proposer une vente de lave-linge à un commercial B2B.
                    const typesDuCompte = new Set(
                      [...publicTargets, ...privateTargets].map((t: any) => t.caseType).filter(Boolean),
                    );
                    const banqueTargets = allTargets
                      .filter(isSocle)
                      .filter((t: any) => typesDuCompte.size === 0 || typesDuCompte.has(t.caseType))
                      .slice().sort((a: any, b: any) => {
                      // Le cas conseillé passe DEVANT tout le reste : le groupe n'affiche que
                      // deux cartes avant de se replier, un conseil en troisième position ne se
                      // verrait pas — et une recommandation qu'il faut aller chercher n'en est
                      // plus une.
                      const conseille = (t: any) => (recommandation?.targetId === t.id ? 0 : 1);
                      if (conseille(a) !== conseille(b)) return conseille(a) - conseille(b);
                      const jamaisJoue = (t: any) => ((t.playCount ?? 0) === 0 ? 0 : 1);
                      if (jamaisJoue(a) !== jamaisJoue(b)) return jamaisJoue(a) - jamaisJoue(b);
                      const natif = (t: any) => (t.simulatorFits?.find((f: any) => f.simulatorId === agent.id)?.fitLevel === "native" ? 0 : 1);
                      if (natif(a) !== natif(b)) return natif(a) - natif(b);
                      return (DIFF_ORDER[a.suggestedDifficulty ?? "medium"] ?? 1) - (DIFF_ORDER[b.suggestedDifficulty ?? "medium"] ?? 1);
                    });

                    // Ce qu'une capsule et la popup ont en commun : titre, couleurs, pastilles.
                    const infosDuCas = (target: any) => {
                      const isPrivate = target.visibility === "private";
                      let industryLabel = "";
                      try {
                        const industries = JSON.parse(target.industry || "[]");
                        if (Array.isArray(industries)) industryLabel = industries.map((s: string) => localizeIndustry(s, i18n.language)).join(" · ");
                        else industryLabel = localizeIndustry(target.industry || "", i18n.language);
                      } catch {
                        industryLabel = localizeIndustry(target.industry || "", i18n.language);
                      }
                      const personaeList = target.personae || [];
                      const targetOffers = (target as any).offers as Array<{ id: string; name: string; description?: string | null }> | undefined;
                      const isB2C = target.caseType === "B2C";
                      // Couleur d'accent : violet pour cas privé, sinon B2B/B2C
                      const accentColor = isPrivate ? "#5A4BBE" : (isB2C ? "#1E7A3C" : "#384872");
                      const accentBg = isPrivate ? "#988EF2" : (isB2C ? "#7CBF94" : "#A6D3F9");
                      // Priorité au nom libre du cas (Mon compte → wizard ou rename inline),
                      // qu'il soit privé ou non. Fallback : offre (B2C) ou companySize (B2B).
                      // Un cas de la banque a son titre dans les deux langues :
                      // `pickCasLegacyField` retombe sur le français si l'anglais manque.
                      const titre = pickCasLegacyField(target, "displayName", i18n.language)
                        || (isB2C
                          ? (targetOffers && pickOfferLabel(targetOffers[0], i18n.language)) || t("training.b2cCase")
                          : (localizeCompanySize(target.companySize, i18n.language) || t("account.companySize")));
                      // Badge source du cas privé (2026-06-11, aligné AccountPage).
                      // Coach Debrief : sourceRealCallSessionId renseigné (violet).
                      // Coach Prepa : private sans sourceRealCallSessionId (jaune).
                      const pastilles = (
                        <>
                          {isPrivate && (
                            target.sourceRealCallSessionId ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                style={{ background: "#988EF2", color: "white" }}
                                title={isFr ? "Cas créé via Coach Debrief" : "Created from Coach Debrief"}
                              >
                                🎙️ {isFr ? "Coach Debrief" : "Coach Debrief"}
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                style={{ background: "#F6C847", color: "#926B00" }}
                                title={isFr ? "Cas créé via Coach Prepa" : "Created from Coach Prepa"}
                              >
                                📋 {isFr ? "Coach Prepa" : "Coach Prepa"}
                              </span>
                            )
                          )}
                          <span
                            className="inline-block text-[10px] font-bold px-2 py-0.5 rounded"
                            style={{ backgroundColor: accentBg, color: accentColor }}
                          >
                            {target.caseType || "B2B"}
                          </span>
                        </>
                      );
                      // LE cas conseillé — un seul, et il dit pourquoi. Une étoile qui marque,
                      // pas une note sur trois : « ★☆☆ » se lirait « mauvais cas » là où on veut
                      // dire « pas pour vous, maintenant ».
                      const estConseille = recommandation?.targetId === target.id;
                      return { isPrivate, industryLabel, personaeList, targetOffers, isB2C, accentColor, accentBg, titre, pastilles, estConseille };
                    };

                    // Détail complet d'un cas : affiché dans la popup, jamais dans la liste.
                    const renderCaseDetails = (target: any) => {
                      const { isPrivate, personaeList, targetOffers, isB2C, estConseille } = infosDuCas(target);
                      const personaeCount = personaeList.length;
                      const targetContext = (target as any).context as { nameFr: string; nameEn: string; descriptionFr?: string | null; descriptionEn?: string | null } | null;

                      // Une ligne « libellé : valeur » du détail d'un interlocuteur ; rien si vide.
                      const ligne = (label: string, valeur: React.ReactNode) =>
                        valeur ? (
                          <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line break-words">
                            <span className="font-semibold text-slate-700">{label}{isFr ? " : " : ": "}</span>{valeur}
                          </p>
                        ) : null;
                      // Style de personnalité (« Variable », ou catégorie › style pour chacun des
                      // styles fixés), maturité et pression interne — quand ils sont définis.
                      const renderProfilPsy = (p: any) => {
                        const { variable, styles } = getPersonaPersonalityLevels(p, i18n.language);
                        const style = variable
                          ? t("persona.personalityModeVariable")
                          : styles.map((s) => (s.category ? `${s.category} › ${s.style}` : s.style)).join(" / ");
                        return (
                          <>
                            {ligne(t("persona.personalityStyle"), style)}
                            {ligne(t("persona.maturityLevel"), localizeMaturityLevel(p.maturityLevel || p.maturity_level, i18n.language))}
                            {ligne(t("persona.internalPressure"), localizeInternalPressure(p.internalPressure || p.internal_pressure, i18n.language))}
                          </>
                        );
                      };

                      const renderPersonaDetail = (p: any, idx: number) => {
                        if (isB2C) {
                          // Un persona B2C se décrivait par sa CSP, son âge et son genre. Ceux de
                          // la banque n'ont ni CSP ni tranche d'âge : la carte restait VIDE.
                          // Le nom prend la tête quand il existe, le reste passe en dessous.
                          const titreB2C = getPersonaLabel(p);
                          const parts = [localizePersonaOption(p.csp, i18n.language), p.ageRange, p.gender].filter(Boolean);
                          return (
                            <div key={p.id || idx} className="rounded-md bg-white border border-slate-200 px-2.5 py-2">
                              {titreB2C && <p className="text-sm font-medium text-slate-800">{titreB2C}</p>}
                              {parts.length > 0 && (
                                <p className={titreB2C ? "text-xs text-slate-600 mt-0.5" : "text-sm font-medium text-slate-800"}>{parts.join(" · ")}</p>
                              )}
                              {ligne(t("persona.decisionCriteria"), localizePersonaOption(p.purchaseCriteria || p.purchase_criteria, i18n.language))}
                              {ligne(t("persona.purchasePhase"), localizePersonaOption(p.purchasePhase || p.purchase_phase, i18n.language))}
                              {ligne(t("account.productRelationship"), localizePersonaOption(p.productRelationship || p.product_relationship, i18n.language))}
                              {renderProfilPsy(p)}
                            </div>
                          );
                        }
                        // Titre : `getPersonaLabel` fait primer le nom quand il existe — c'est le
                        // cas des personae de la banque, où l'interlocuteur est nommé (« Nadia
                        // Berthaud »). Sa fonction passe alors en sous-titre : un cas se retient
                        // par son interlocuteur, pas par un intitulé de poste.
                        const personaTitre = getPersonaLabel(p);
                        const personaFonction = localizePersonaOption(pickPersonaField(p, "function", i18n.language), i18n.language);
                        return (
                          <div key={p.id || idx} className="rounded-md bg-white border border-slate-200 px-2.5 py-2">
                            <p className="text-sm font-medium text-slate-800">
                              {personaTitre || personaFonction || t("training.personaWithoutFunction")}
                            </p>
                            {personaTitre && personaFonction && personaTitre !== personaFonction && (
                              <p className="text-xs text-slate-600 mt-0.5">{personaFonction}</p>
                            )}
                            {(p.decisionMakerProfile || p.decision_maker_profile) && (
                              <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line break-words">
                                {pickDecisionMaker(p)}
                              </p>
                            )}
                            {pickPersonaField(p, "painPoints", lang) && (
                              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line break-words italic">{pickPersonaField(p, "painPoints", lang)}</p>
                            )}
                            {renderProfilPsy(p)}
                          </div>
                        );
                      };

                      return (
                        <div className="flex flex-col gap-4">
                          {/* La raison de la mise en avant. Sans elle, le bandeau ne serait
                              qu'un cas placé plus haut, que rien ne distingue d'un hasard. */}
                          {estConseille && recommandation && (
                            <p className="rounded-md px-3 py-2 text-xs" style={{ backgroundColor: "#F6C84733", color: "#926B00" }}>
                              <span aria-hidden="true">★</span> {phraseRecommandation(recommandation.motif, evaluationCriteria)}
                            </p>
                          )}

                          {/* Focus du brief IA pour les cas privés */}
                          {isPrivate && target.focusRaw && (
                            <p className="text-sm italic text-slate-700 whitespace-pre-line break-words">{target.focusRaw}</p>
                          )}

                          {/* Offre(s) */}
                          {targetOffers && targetOffers.length > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                                <span>💼</span>
                                {targetOffers.length > 1 ? t("training.offers") : t("training.offer")}
                              </p>
                              <div className="rounded-md bg-white border border-slate-200 px-2.5 py-2">
                                <p className="text-sm font-medium text-slate-800">{targetOffers.map((o) => pickOfferLabel(o, i18n.language)).join(", ")}</p>
                                {pickOfferDescription(targetOffers[0], i18n.language) && (
                                  <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line break-words">
                                    {pickOfferDescription(targetOffers[0], i18n.language)}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Persona(s) — détail complet pour CHACUN */}
                          {personaeCount > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                                <span>👤</span>
                                {personaeCount === 1
                                  ? t("training.persona")
                                  : t("training.personaeCount", { count: personaeCount })}
                              </p>
                              <div className="space-y-1.5">
                                {personaeList.map((p: any, idx: number) => renderPersonaDetail(p, idx))}
                              </div>
                            </div>
                          )}

                          {/* Contexte */}
                          {targetContext && (
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                                <span>📍</span>
                                {t("training.contextLabel")}
                              </p>
                              <div className="rounded-md bg-white border border-slate-200 px-2.5 py-2">
                                <p className="text-sm font-medium text-slate-800">
                                  {isFr ? (targetContext.nameFr || targetContext.nameEn) : (targetContext.nameEn || targetContext.nameFr)}
                                </p>
                                {(isFr ? targetContext.descriptionFr : targetContext.descriptionEn) && (
                                  <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line break-words">
                                    {isFr ? targetContext.descriptionFr : targetContext.descriptionEn}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Ce que ce cas pèse plus lourd — l'information existait en base et
                              partait au scoring sans jamais être montrée à celui qui allait être
                              noté dessus. Trois noms qu'on retient valent mieux que quatorze
                              qu'on survole. */}
                          {(() => {
                            const focus = criteresSurPonderes(target, evaluationCriteria);
                            if (focus.length === 0) return null;
                            return (
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#5A4BBE" }}>
                                  {t("training.caseFocus")}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {focus.map((c) => (
                                    <span key={c} className="text-[11px] rounded-full px-2 py-0.5"
                                      style={{ backgroundColor: "#988EF226", color: "#5A4BBE" }}>{c}</span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Stats pour les cas privés (couture Sales Coach Réel → entraînement) */}
                          {isPrivate && (target.playCount ?? 0) > 0 && (
                            <div className="flex items-center gap-3 text-[11px] pt-2 border-t border-slate-200" style={{ color: "#6B7280" }}>
                              <span>🔁 {t("training.playedCount", { count: target.playCount })}</span>
                              {target.avgScoreGlobal != null && (
                                <span>{t("training.avgScore")} : {target.avgScoreGlobal}/100</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    };

                    // Capsule : l'essentiel d'un cas, sur une hauteur fixe. Un clic CHOISIT le cas
                    // (suite : niveau de difficulté) ; « Détails du cas » ouvre la popup pour tout
                    // lire avant de choisir (décision Valérie, 21/10/2026).
                    const renderCaseCapsule = (target: any) => {
                      const { isPrivate, industryLabel, personaeList, targetOffers, isB2C, accentBg, accentColor, titre, pastilles, estConseille } = infosDuCas(target);
                      const interlocuteurs = personaeList
                        .map((p: any) => getPersonaLabel(p) || localizePersonaOption(pickPersonaField(p, "function", i18n.language), i18n.language))
                        .filter(Boolean)
                        .join(", ");
                      const offre = targetOffers && targetOffers.length > 0
                        ? targetOffers.map((o) => pickOfferLabel(o, i18n.language)).join(", ")
                        : "";
                      return (
                        <div
                          key={target.id}
                          data-testid="case-capsule"
                          className="w-full h-full rounded-xl bg-card overflow-hidden hover:shadow-lg transition-all flex flex-col"
                          style={{
                            border: `1px solid #e2e8f0`,
                            borderLeftWidth: "4px",
                            borderLeftColor: estConseille ? "#F6C847" : accentBg,
                            ...(isPrivate && { background: "linear-gradient(135deg, #FFFFFF 0%, #F8F7FF 100%)" }),
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleTargetSelect(target)}
                            data-testid="case-select"
                            className="flex-1 w-full text-left flex flex-col"
                          >
                          {estConseille && (
                            <div
                              className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
                              style={{ backgroundColor: "#F6C84733", color: "#926B00" }}
                            >
                              <span aria-hidden="true">★</span>
                              {t("training.recommendedForYou")}
                            </div>
                          )}
                          <div className="flex-1 px-3 py-2.5 flex flex-col gap-1.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-1.5 flex-wrap">{pastilles}</div>
                              <ChevronRight className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                            </div>
                            <p className="font-bold text-sm line-clamp-2" style={{ color: accentColor }}>{titre}</p>
                            {!isB2C && industryLabel && !isPrivate && (
                              <p className="text-xs line-clamp-1" style={{ color: accentColor, opacity: 0.8 }}>{industryLabel}</p>
                            )}
                            {isPrivate && target.focusRaw && (
                              <p className="text-xs line-clamp-2 italic" style={{ color: accentColor, opacity: 0.85 }}>{target.focusRaw}</p>
                            )}
                            {interlocuteurs && (
                              <p className="text-xs text-slate-700 line-clamp-1">👤 {interlocuteurs}</p>
                            )}
                            {offre && !isB2C && (
                              <p className="text-xs text-slate-600 line-clamp-1">💼 {offre}</p>
                            )}
                          </div>
                          </button>
                          <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                            {/* Jamais joué : une information utile, sans mettre la capsule en
                                concurrence avec celle qui est conseillée. */}
                            <span>
                              {(target.playCount ?? 0) === 0
                                ? t("training.neverPlayedShort")
                                : t("training.playedCount", { count: target.playCount })}
                            </span>
                            <button
                              type="button"
                              onClick={() => setCasOuvert(target)}
                              data-testid="case-details"
                              className="font-medium hover:underline px-1 py-0.5"
                              style={{ color: accentColor }}
                            >
                              {t("training.caseDetails")}
                            </button>
                          </div>
                        </div>
                      );
                    };

                    // Trois provenances en onglets, l'un à côté de l'autre, dans l'ordre décidé
                    // par Valérie le 20/10/2026 : d'abord les cas de l'entreprise, puis ceux de
                    // ses rendez-vous, enfin les cas génériques. Chaque onglet affiche son
                    // nombre de cas : rien n'est caché sans qu'on le sache.
                    //
                    // Les libellés disent l'ORIGINE du cas, pas son propriétaire : « cas
                    // personnels » évoquait la confidentialité alors que ces cas viennent d'un
                    // vrai rendez-vous préparé ou débriefé.
                    const GROUPES: Array<{
                      id: "company" | "meetings" | "kestio";
                      emoji: string; couleur: string; cas: any[];
                      titreKey: string; aideKey: string; videKey: string; videAideKey: string;
                    }> = [
                      { id: "company", emoji: "🏢", couleur: "#384872", cas: publicTargets,
                        titreKey: "training.originCompany", aideKey: "training.originCompanyHint",
                        videKey: "training.emptyCompany", videAideKey: "training.emptyCompanyHint" },
                      { id: "meetings", emoji: "📞", couleur: "#5A4BBE", cas: privateTargets,
                        titreKey: "training.originMeetings", aideKey: "training.originMeetingsHint",
                        videKey: "training.emptyMeetings", videAideKey: "training.emptyMeetingsHint" },
                      { id: "kestio", emoji: "✨", couleur: "#1E7A3C", cas: banqueTargets,
                        titreKey: "training.originKestio", aideKey: "training.originKestioHint",
                        videKey: "training.emptyKestio", videAideKey: "training.emptyKestioHint" },
                    ];

                    // Onglet ouvert : celui choisi, sinon le premier qui a du contenu (dans
                    // l'ordre ci-dessus) — ouvrir sur un onglet vide quand un autre est rempli
                    // ferait croire qu'il n'y a rien à jouer.
                    const groupeActif = GROUPES.find((g) => g.id === ongletCas)
                      ?? GROUPES.find((g) => g.cas.length > 0)
                      ?? GROUPES[0]!;
                    const ongletConseille = GROUPES.find((g) => g.cas.some((c) => c.id === recommandation?.targetId))?.id;

                    const g = groupeActif;
                    const casDetail = casOuvert ? infosDuCas(casOuvert) : null;

                    return (
                      <div>
                        <SubTabs
                          items={GROUPES.map((g) => ({
                            id: g.id,
                            icon: g.emoji,
                            label: `${t(g.titreKey)} (${g.cas.length})${ongletConseille === g.id ? " ★" : ""}`,
                            tourId: `case-tab-${g.id}`,
                          }))}
                          activeId={groupeActif.id}
                          onSelect={(id) => setOngletCas(id)}
                          accent={groupeActif.couleur}
                          ariaLabel={t("account.chooseTargetPersona")}
                          className="mx-auto"
                        />

                        {g.cas.length === 0 ? (
                          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-8 text-center">
                            <div className="mx-auto mb-2.5 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm text-xl">
                              {g.emoji}
                            </div>
                            <p className="text-sm font-semibold text-slate-700">{t(g.videKey)}</p>
                            <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">{t(g.videAideKey)}</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-muted-foreground text-center mb-3">{t(g.aideKey)}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {g.cas.map(renderCaseCapsule)}
                            </div>
                          </>
                        )}

                        {casOuvert && casDetail && (
                          <CaseDetailDialog
                            title={casDetail.titre}
                            accentBg={casDetail.accentBg}
                            badges={casDetail.pastilles}
                            subtitle={!casDetail.isB2C && casDetail.industryLabel && !casDetail.isPrivate ? casDetail.industryLabel : undefined}
                            onClose={() => setCasOuvert(null)}
                            footer={
                              <>
                                {/* Un cas Kestio se joue tel quel, ou sert de point de départ.
                                    « Partir de ce cas » en fait une copie éditable du compte : on
                                    remplace l'offre et l'interlocuteur par les siens, le cadre
                                    pédagogique reste. */}
                                {(() => {
                                  const target = casOuvert;
                                  if (target.kind !== "socle" || !peutForker) return null;
                                  return (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={forkEnCours === target.id}
                                      onClick={async () => {
                                        setForkEnCours(target.id);
                                        try {
                                          const copie = await accountApi.forkTarget(target.id);
                                          const rafraichis = await accountApi.getTargets();
                                          setAllTargets(rafraichis.filter((t: any) => (t.personae || []).length > 0));
                                          // La copie rejoint « Cas de mon entreprise » : on ouvre
                                          // cet onglet pour qu'elle soit visible sans chercher.
                                          setOngletCas("company");
                                          setCasOuvert(null);
                                          if (copie) handleTargetSelect({ ...copie, personae: copie.personae || [] });
                                        } catch (e: any) {
                                          alert(e?.message || t("api.updateError"));
                                        } finally {
                                          setForkEnCours(null);
                                        }
                                      }}
                                      className="text-[#5A4BBE]"
                                    >
                                      {forkEnCours === target.id ? t("training.forkInProgress") : t("training.forkFromKestio")}
                                    </Button>
                                  );
                                })()}
                                <Button
                                  size="sm"
                                  onClick={() => { const cible = casOuvert; setCasOuvert(null); handleTargetSelect(cible); }}
                                  className="bg-[#384872] hover:bg-[#2c3a5e] text-white"
                                  data-testid="case-play"
                                >
                                  {t("training.playThisCase")}
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </>
                            }
                          >
                            {renderCaseDetails(casOuvert)}
                          </CaseDetailDialog>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Step 2: Choose persona within selected target */
                <>
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: agentColor + "15" }}>
                      <User className="h-6 w-6" style={{ color: agentColor }} />
                    </div>
                    <h3 className="font-semibold text-lg">{t("account.selectPersona")}</h3>
                    <button
                      onClick={() => { setSelectedTarget(null); setTargetSelectStep("target"); }}
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mt-1"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      {selectedTarget?.caseType === "B2C"
                        ? (() => {
                            const names = (selectedTarget?.offerIds || []).map((oid: string) => pickOfferLabel(trainingOffers.find((o: any) => o.id === oid), i18n.language)).filter(Boolean);
                            return names.length > 0 ? names.join(", ") : "B2C";
                          })()
                        : selectedTarget?.companySize
                      }
                      {selectedTarget?.caseType !== "B2C" && (() => {
                        try {
                          const industries = JSON.parse(selectedTarget?.industry || "[]");
                          if (Array.isArray(industries) && industries.length > 0) return ` · ${industries.join(" · ")}`;
                        } catch { /* ignore */ }
                        return "";
                      })()}
                    </button>
                  </div>
                  <div className="w-full max-w-sm space-y-2">
                    {(selectedTarget?.personae || []).map((persona) => {
                      const isExpanded = expandedPersonaId === persona.id;
                      const hasDetails = persona.decisionMakerProfile || persona.painPoints || persona.contactReason || persona.expressionStyle || persona.mainExpectation;
                      return (
                        <div
                          key={persona.id}
                          className={`rounded-xl border bg-card transition-all ${isExpanded ? "border-primary/40 shadow-md" : "border-border"}`}
                        >
                          <div className="flex items-center p-4">
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handlePersonaSelect(persona)}>
                              <p className="font-semibold text-sm">
                                {(persona.category || persona.category) === "customer_care"
                                  ? ((persona.personaType || persona.persona_type) === "B2C"
                                      ? (localizePersonaOption(persona.contactReason || persona.contact_reason, i18n.language) || t("training.personaCustomerCareDefault"))
                                      : (localizePersonaOption(pickPersonaField(persona, "function", i18n.language), i18n.language) || t("training.personaCustomerCareDefault")))
                                  : ((persona.personaType || persona.persona_type) === "B2C"
                                      ? (localizePersonaOption(persona.csp, i18n.language) || localizePersonaOption(pickPersonaField(persona, "function", i18n.language), i18n.language) || t("training.personaB2CDefault"))
                                      : (localizePersonaOption(pickPersonaField(persona, "function", i18n.language), i18n.language) || t("training.personaDefault")))
                                }
                              </p>
                              {(persona.personaType || persona.persona_type) === "B2C" && persona.ageRange && (
                                <p className="text-xs text-muted-foreground">{persona.ageRange}</p>
                              )}
                              {(persona.contactReason || persona.contact_reason) && (
                                <p className="text-xs text-muted-foreground">
                                  {[localizePersonaOption(persona.contactReason || persona.contact_reason, i18n.language), localizePersonaOption(persona.expressionStyle || persona.expression_style, i18n.language), localizePersonaOption(persona.mainExpectation || persona.main_expectation, i18n.language)].filter(Boolean).join(" • ")}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              {hasDetails && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExpandedPersonaId(isExpanded ? null : persona.id); }}
                                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                                  title={t("common.details")}
                                >
                                  {isExpanded
                                    ? <ChevronDown className="h-4 w-4 text-primary" />
                                    : <Info className="h-4 w-4 text-muted-foreground" />
                                  }
                                </button>
                              )}
                              <button
                                onClick={() => handlePersonaSelect(persona)}
                                className="px-3 py-1 rounded-lg text-xs font-medium text-white transition-colors"
                                style={{ backgroundColor: agentColor }}
                              >
                                {t("common.choose")}
                              </button>
                            </div>
                          </div>
                          {isExpanded && hasDetails && (
                            <div className="px-4 pb-4 pt-0 space-y-2 border-t border-border/50 mt-0 pt-3">
                              {persona.decisionMakerProfile && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-0.5">{t("account.decisionMaker")}</p>
                                  <p className="text-sm">{pickDecisionMaker(persona)}</p>
                                </div>
                              )}
                              {pickPersonaField(persona, "painPoints", lang) && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-0.5">{t("account.painPoints")}</p>
                                  <p className="text-sm">{pickPersonaField(persona, "painPoints", lang)}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Difficulty selection */}
          {isDifficultySelect && (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full overflow-hidden flex items-center justify-center mb-4 sm:mb-6" style={{ backgroundColor: agentColor + "20" }}>
                {agent.image ? (
                  <img src={agent.image} alt={agent.name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: 'top' }} />
                ) : (
                  <span className="text-2xl sm:text-3xl">{agent.icon}</span>
                )}
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 text-center">{t("training.chooseDifficulty")}</h3>
              {selectedPersona && (
                <p className="text-xs text-muted-foreground mb-2 text-center">
                  {selectedTarget?.caseType === "B2C" ? (
                    <>
                      {(() => { const names = (selectedTarget?.offerIds || []).map((oid: string) => pickOfferLabel(trainingOffers.find((o: any) => o.id === oid), i18n.language)).filter(Boolean); return names.length > 0 ? <span className="font-medium">{names.join(", ")}</span> : null; })()}
                      {" — "}
                      <span className="font-medium">{localizePersonaOption(selectedPersona.csp, i18n.language) || localizePersonaOption(pickPersonaField(selectedPersona, "function", i18n.language), i18n.language)}</span>
                      {selectedPersona.ageRange && <span className="text-muted-foreground"> ({selectedPersona.ageRange})</span>}
                    </>
                  ) : (
                    <>
                      {selectedTarget?.companySize && <span className="font-medium">{localizeCompanySize(selectedTarget.companySize, i18n.language)}</span>}
                      {selectedTarget?.companySize && pickPersonaField(selectedPersona, "function", i18n.language) && " — "}
                      <span className="font-medium">{localizePersonaOption(pickPersonaField(selectedPersona, "function", i18n.language), i18n.language)}</span>
                    </>
                  )}
                </p>
              )}
              <p className="text-muted-foreground text-xs sm:text-sm mb-4 sm:mb-6 text-center max-w-md">
                {agent.name?.toLowerCase().includes("satisfactor")
                  ? t("training.difficultyExplainSatisfactor")
                  : t("training.difficultyExplain")}
              </p>
              {(() => {
                const levels = agent.name?.toLowerCase().includes("satisfactor") ? [
                  { value: "easy", label: t("training.easy"), desc: t("training.easyDescSatisfactor") },
                  { value: "medium", label: t("training.medium"), desc: t("training.mediumDescSatisfactor") },
                  { value: "hard", label: t("training.hard"), desc: t("training.hardDescSatisfactor") },
                ] : [
                  { value: "easy", label: t("training.easy"), desc: t("training.easyDesc") },
                  { value: "medium", label: t("training.medium"), desc: t("training.mediumDesc") },
                  { value: "hard", label: t("training.hard"), desc: t("training.hardDesc") },
                ];
                // Niveau imposé par la phase de parcours → sélection verrouillée.
                const locked: string | null = pathContext?.difficulty && pathContext.difficulty !== "" ? pathContext.difficulty : null;
                const lockedLabel = locked ? (levels.find((l) => l.value === locked)?.label ?? locked) : "";
                // Niveau pour lequel le cas a été ÉCRIT (décision du 26/08 : le cas propose,
                // l'utilisateur garde la main). Ses objections et son enjeu caché sont calibrés
                // dessus — le jouer ailleurs, c'est jouer autre chose que ce que l'auteur a écrit.
                // Masqué quand un parcours verrouille : deux marques opposées sur le même écran
                // (« conseillé » en vert, « imposé » en violet) ne se lisent plus.
                const ecritPour: string | null = locked
                  ? null
                  : (levels.some((l) => l.value === (selectedTarget as any)?.suggestedDifficulty)
                      ? ((selectedTarget as any).suggestedDifficulty as string)
                      : null);
                return (
                  <>
                    {locked && (
                      <div className="mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium" style={{ background: "#988EF222", border: "1px solid #988EF2", color: "#5A4BBE" }}>
                        <Lock className="h-4 w-4 shrink-0" />
                        {t("training.difficultyLocked", { level: lockedLabel })}
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                      {levels.map((level) => {
                        const isImposed = locked === level.value;
                        const disabled = !!locked && !isImposed;
                        const suggere = ecritPour === level.value;
                        return (
                          <button
                            key={level.value}
                            disabled={disabled}
                            onClick={() => !disabled && handleDifficultySelect(level.value)}
                            className={`flex flex-row sm:flex-col items-center gap-2 rounded-xl border-2 px-4 py-3 sm:px-6 sm:py-4 transition-all sm:min-w-[140px] ${
                              isImposed || suggere
                                ? "shadow-md"
                                : disabled
                                  ? "border-muted opacity-40 cursor-not-allowed"
                                  : "border-muted hover:border-primary hover:bg-primary/5"
                            }`}
                            style={
                              isImposed ? { borderColor: "#5A4BBE", background: "#988EF220" }
                              : suggere ? { borderColor: "#1E7A3C", background: "#7CBF9420" }
                              : undefined
                            }
                          >
                            <span
                              className="font-semibold text-sm sm:text-base flex items-center gap-1"
                              style={isImposed ? { color: "#5A4BBE" } : suggere ? { color: "#1E7A3C" } : undefined}
                            >
                              {isImposed && <Lock className="h-3.5 w-3.5" />}{level.label}
                            </span>
                            <span className="text-xs text-muted-foreground text-center">{level.desc}</span>
                            {isImposed && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "#5A4BBE" }}>
                                {t("training.difficultyImposed")}
                              </span>
                            )}
                            {suggere && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-center" style={{ color: "#1E7A3C" }}>
                                {t("training.difficultyWrittenFor")}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Prospect > Scénario */}
          {mainTab === "prospect" && subTab === "scenario" && phase !== "init" && phase !== "scenario-loading" && phase !== "difficulty-select" && phase !== "target-select" && (
            <div className="space-y-4">
              {/* Pendant l'appel : la scène "visio" (avatar + sous-titre live + waveform)
                  remplace le contexte statique — c'est l'onglet visible durant l'appel
                  (le sous-onglet "Appel" n'apparaît qu'après, pour la relecture).
                  Depuis 22/07/26 : panneau "Barres de progression" à droite (maquette
                  validée Fabien+Valérie) — ouvert par défaut, pliable. Composant
                  agnostique du transport ; les values seront alimentées quand le
                  back de réception du webhook N8N chuchoteur sera en place. */}
              {phase === "calling" ? (
                <div className="flex gap-0 rounded-lg border bg-white overflow-hidden">
                  <div className="flex-1 min-w-0">
                    <CallStage
                      prospectName={scenario?.prospectName || agent.name}
                      subtitle={[agent.name, scenario?.companyName].filter(Boolean).join(" · ")}
                      avatarUrl={scenario?.avatarUrl}
                      isSpeaking={realtimeSession.isAgentSpeaking}
                      liveTranscript={realtimeSession.liveTranscript}
                      agentStream={agentStream}
                      color={agentColor}
                      t={t}
                    />
                  </div>
                  {sidebandVisible && (
                  <LiveFeedbackPanel
                    criteria={evaluationCriteria}
                    values={liveFeedbackValues}
                  />
                  )}
                </div>
              ) : scenario ? (
                <>
                  {/* Brief de session en 4 sections — composant PARTAGÉ avec l'historique,
                      pour que le même scénario se présente à l'identique des deux côtés.
                      Ici les données viennent du cas EN COURS ; dans l'historique, elles
                      viennent du scénario figé (le cas a pu changer depuis). */}
                  {(() => {
                    const sellOffers = ((selectedTarget as any)?.offers as Array<any> | undefined) || [];
                    const isB2C = ((selectedTarget as any)?.caseType || "").toUpperCase() === "B2C";
                    const fn = localizePersonaOption(pickPersonaField(selectedPersona, "function", i18n.language), i18n.language);
                    const csp = localizePersonaOption((selectedPersona as any)?.csp || "", i18n.language);
                    const age = (selectedPersona as any)?.ageRange || "";
                    const industryRaw = pickDisplayIndustry(scenario.selectedIndustry, (selectedTarget as any)?.industry);
                    const industryLabel = industryRaw ? localizeIndustry(industryRaw, i18n.language) : "";
                    const sizeLabel = localizeCompanySize((selectedTarget as any)?.companySize, i18n.language);
                    // Style RÉELLEMENT joué : lu dans l'instantané du scénario, pas sur le
                    // persona — en mode « variable » BizCoach fait tourner les styles à chaque
                    // session, le persona ne dit donc pas lequel a été retenu cette fois-ci.
                    const styleJoue = buildBriefFromVariables(scenario).personalityStyle;
                    // Langue d'entraînement ≠ langue d'interface : tout le brief suit la
                    // langue d'entraînement, titres compris (décision du 21/09/2026). Les
                    // textes de la base sont alors remplacés par ceux que N8N a rédigés.
                    const briefLang = scenario.spokenLanguage || null;
                    const shown = applySpokenBrief({
                      offerLabel: sellOffers.map((o) => pickOfferLabel(o, i18n.language)).join(", "),
                      offerDescription: pickOfferDescription(sellOffers[0], i18n.language),
                      interlocutorSubtitle: isB2C ? [csp, age].filter(Boolean).join(" · ") : fn,
                      personalityStyle: styleJoue,
                      industryLabel,
                      companySizeLabel: sizeLabel,
                    }, briefLang ? scenario.briefSpoken : null);
                    return (
                      <ScenarioBrief
                        prospectName={scenario.prospectName}
                        companyName={scenario.companyName}
                        companyDescription={scenario.prospectCompanyDescription}
                        priorContext={scenario.priorContext}
                        legacyFullText={scenario.userMessage}
                        offerLabel={shown.offerLabel}
                        offerDescription={shown.offerDescription}
                        offerCount={sellOffers.length}
                        interlocutorSubtitle={shown.interlocutorSubtitle}
                        personalityStyle={shown.personalityStyle}
                        companyMeta={[shown.industryLabel, shown.companySizeLabel].filter(Boolean).join(" · ")}
                        isB2C={isB2C}
                        focusCriteria={criteresSurPonderes(selectedTarget, evaluationCriteria)}
                        labels={briefLabelsForLanguage(
                          briefLang ?? i18n.language,
                          i18n.getFixedT(briefLang === "fr" || briefLang === "en" ? briefLang : i18n.language),
                        )}
                      />
                    );
                  })()}
                </>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  {t("training.noScenario")}
                </div>
              )}
            </div>
          )}

          {/* Prospect > Appel */}
          {mainTab === "prospect" && subTab === "call" && phase !== "init" && phase !== "scenario-loading" && phase !== "difficulty-select" && phase !== "target-select" && (
            <div className="space-y-3">
              {messages.length === 0 && phase === "ready" && (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: agentColor + "20" }}>
                    <Phone className="h-10 w-10" style={{ color: agentColor }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{t("training.readyForCall")}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t("training.clickToStartCall")}
                  </p>
                </div>
              )}

              {/* Historique des bulles — masqué pendant l'appel (sous-titre live seul),
                  réaffiché ensuite pour la relecture du débrief. */}
              {phase !== "calling" && messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Sales Coach */}
          {mainTab === "coach" && (
            <>
              {!callEnded ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#988EF2' }}>
                    <span className="text-4xl">🎙️</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{t("training.salesCoachAI")}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {callStarted
                      ? t("training.listeningCoach")
                      : t("training.startCallFirst")}
                  </p>
                </div>
              ) : phase === "feedback-loading" ? (
                <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="font-semibold mb-1" style={{ color: "#384872" }}>
                    {t("training.analyzing")}
                  </p>
                  <p className="text-sm text-muted-foreground max-w-md mb-3">
                    {t("training.feedbackLoadingDescription")}
                  </p>
                  <p className="text-xs italic" style={{ color: "#9CA3AF" }}>
                    {t("training.feedbackLoadingTip")}
                  </p>
                </div>
              ) : coachSubTab === "feedback" && feedback ? (
                <div className="flex flex-col h-full">
                  <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pb-2">
                    {/* Mobile : mini-cartes empilées */}
                    <div className="flex flex-col gap-2 md:hidden">
                      {scoringCriteriaForTotal.map((c) => {
                        const val = Number(feedback.scores?.[c.key]) || 0;
                        const m = c.max || 10;
                        const color = getScoreColor(val, m);
                        return (
                          <div key={c.key} className="rounded-lg bg-muted/50 px-3 py-2 flex items-center gap-3">
                            <span className="text-xs text-muted-foreground flex-1">{getLabel(c)}</span>
                            {drillableKeys.has(c.key) && (
                              <DrillIcon onClick={() => launchDrillFromFeedback(c)} highlighted={relanceCard?.weakestCriterionKey === c.key} tooltip={t("drill.iconTooltip")} tooltipPlacement="bottom" />
                            )}
                            <Progress value={(val / m) * 100} className="h-1.5 w-20" indicatorClassName={color.bg} />
                            <span className={`text-sm font-bold ${color.text} w-10 text-right`}>{val}/{m}</span>
                          </div>
                        );
                      })}
                      {booleanCriteria.map(c => {
                        const val = feedback.scores?.[c.key];
                        return val ? (
                          <Badge key={c.key} className="bg-kestio-green text-white w-fit text-xs px-2 py-0.5">✓ {getLabel(c)}</Badge>
                        ) : null;
                      })}
                      {scoringCriteria.filter(c => overallKeys.includes(c.key)).map((c) => {
                        const val = Number(feedback.scores?.[c.key]) || 0;
                        const m = c.max || 10;
                        const passed = c.successThreshold != null && val >= c.successThreshold;
                        const hasThreshold = c.successThreshold != null;
                        const verdictLabel = hasThreshold
                          ? passed
                            ? pickVerdictLabel(c, lang, true) || t("training.verdictPassed")
                            : pickVerdictLabel(c, lang, false) || t("training.verdictFailed")
                          : null;
                        return (
                          <div key={c.key}>
                            <div className="rounded-lg bg-kestio-blue/10 border border-kestio-blue/30 px-3 py-3 flex items-center gap-3 mt-1">
                              <span className="text-xs text-kestio-blue italic flex-1">{getLabel(c)}</span>
                              <Progress value={(val / m) * 100} className="h-1.5 w-20" indicatorClassName="bg-kestio-blue" />
                              <span className="text-lg font-bold text-kestio-blue w-10 text-right">{val}/{m}</span>
                            </div>
                            {verdictLabel && (
                              <div className={`mt-1 text-center text-xs font-semibold px-3 py-1.5 rounded-md ${passed ? "bg-kestio-green/10 text-kestio-green" : "bg-kestio-coral/10 text-kestio-coral"}`}>
                                {passed ? "✓" : "✗"} {verdictLabel}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Desktop : cartes avec couleurs dynamiques */}
                    <div className="hidden md:grid md:grid-cols-3 gap-3">
                      {scoringCriteriaForTotal.map((c) => {
                        const val = Number(feedback.scores?.[c.key]) || 0;
                        const m = c.max || 10;
                        const color = getScoreColor(val, m);
                        return (
                          <div key={c.key} className="rounded-xl bg-muted/50 p-3 flex flex-col gap-1.5">
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{getLabel(c)}</span>
                              {drillableKeys.has(c.key) && (
                                <DrillIcon onClick={() => launchDrillFromFeedback(c)} highlighted={relanceCard?.weakestCriterionKey === c.key} tooltip={t("drill.iconTooltip")} tooltipPlacement="bottom" />
                              )}
                            </div>
                            <div className="flex items-end gap-1.5">
                              <span className={`text-2xl font-bold ${color.text} leading-none`}>{val}</span>
                              <span className="text-xs text-muted-foreground mb-0.5">/{m}</span>
                            </div>
                            <Progress value={(val / m) * 100} className="h-1.5" indicatorClassName={color.bg} />
                          </div>
                        );
                      })}
                      {booleanCriteria.map(c => {
                        const val = feedback.scores?.[c.key];
                        return val ? (
                          <Badge key={c.key} className="bg-kestio-green text-white w-fit text-xs px-2 py-1">✓ {getLabel(c)}</Badge>
                        ) : null;
                      })}
                      {scoringCriteria.filter(c => overallKeys.includes(c.key)).map((c) => {
                        const val = Number(feedback.scores?.[c.key]) || 0;
                        const m = c.max || 10;
                        const passed = c.successThreshold != null && val >= c.successThreshold;
                        const hasThreshold = c.successThreshold != null;
                        const verdictLabel = hasThreshold
                          ? passed
                            ? pickVerdictLabel(c, lang, true) || t("training.verdictPassed")
                            : pickVerdictLabel(c, lang, false) || t("training.verdictFailed")
                          : null;
                        return (
                          <div key={c.key} className="col-span-3">
                            <div className="rounded-xl border-2 border-kestio-blue/30 bg-kestio-blue/10 p-4 flex items-center justify-between">
                              <span className="text-sm text-kestio-blue font-medium italic">{getLabel(c)}</span>
                              <div className="flex items-center gap-3">
                                <Progress value={(val / m) * 100} className="h-2 w-32" indicatorClassName="bg-kestio-blue" />
                                <span className="text-3xl font-bold text-kestio-blue">{val}<span className="text-base text-kestio-blue/60">/{m}</span></span>
                              </div>
                            </div>
                            {verdictLabel && (
                              <div className={`mt-2 text-center text-sm font-semibold px-4 py-2 rounded-lg ${passed ? "bg-kestio-green/10 text-kestio-green border border-kestio-green/30" : "bg-kestio-coral/10 text-kestio-coral border border-kestio-coral/30"}`}>
                                {passed ? "✓" : "✗"} {verdictLabel}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Feedback texte du coach */}
                    {feedback.content && (
                      <>
                        <Separator className="my-3" />
                        <h4 className="font-medium text-sm mb-2">{t("history.coachFeedback")}</h4>
                        <SimpleMarkdown text={feedback.content} className="text-sm text-muted-foreground" />
                      </>
                    )}
                  </div>
                </div>
              ) : coachSubTab === "exchanges" ? (
                <div className="space-y-3">
                  {coachMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground text-sm">
                        {phase === "coaching" ? t("training.coachSpeaking") : t("training.noCoachExchanges")}
                      </p>
                    </div>
                  ) : (
                    coachMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  {/* Coach speaking indicator */}
                  {phase === "coaching" && realtimeSession.isAgentSpeaking && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-lg px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                        <span className="text-sm text-muted-foreground">{t("training.coachSpeaking")}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {/* Call Controls — padding bas dynamique pour la barre de gestes iOS (safe-area) */}
      <div
        className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mt-2 sm:mt-4 pt-3 sm:pt-4 border-t px-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
      >
        {isLoading ? (
          /* Loading state */
          <Button size="lg" className="px-8" disabled>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("common.loading")}
          </Button>
        ) : isTargetSelect ? (
          /* Target selection */
          <Button size="lg" variant="outline" onClick={() => {
            if (targetSelectStep === "persona") {
              setSelectedTarget(null);
              setTargetSelectStep("target");
            } else {
              onBack();
            }
          }}>
            {t("common.back")}
          </Button>
        ) : isDifficultySelect ? (
          /* Difficulty selection — controls are in the card above */
          <div className="flex gap-2">
            {allTargets.length > 1 && (
              <Button size="lg" variant="outline" onClick={() => { setSelectedTarget(null); setSelectedPersona(null); setTargetSelectStep("target"); setPhase("target-select"); }}>
                {t("account.changeTarget")}
              </Button>
            )}
            <Button size="lg" variant="outline" onClick={onBack}>
              {t("common.back")}
            </Button>
          </div>
        ) : phase === "ready" ? (
          /* Phase 1 : avant l'appel */
          <Button size="lg" className="px-8" onClick={startCall}>
            <Phone className="mr-2 h-5 w-5" />
            {t("training.startCall")}
          </Button>
        ) : phase === "calling" ? (
          /* Phase 2 : appel prospect en cours */
          <>
            <button
              onClick={toggleMute}
              className={`inline-flex items-center justify-center rounded-full h-12 w-12 transition-colors ${
                isMuted
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-green-500 hover:bg-green-600 text-white"
              }`}
              title={isMuted ? t("training.muted") : t("training.speak")}
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <Button size="lg" variant="destructive" onClick={endCall}>
              <PhoneOff className="mr-2 h-5 w-5" />
              {t("training.hangUp")}
            </Button>
          </>
        ) : phase === "feedback" ? (
          /* Phase 3 : feedback affiché → carte de relance intelligente (hors challenge) */
          <>{renderPostSession()}</>
        ) : phase === "coaching" ? (
          /* Phase 4 : coaching en cours */
          <>
            <button
              onClick={() => { toggleMute(); resetActivity(); }}
              className={`inline-flex items-center justify-center rounded-full h-12 w-12 transition-colors ${
                isMuted
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-green-500 hover:bg-green-600 text-white"
              }`}
              title={isMuted ? t("training.muted") : t("training.speak")}
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <Button
              size="lg"
              onClick={endCoaching}
              className="px-8 ml-4"
            >
              {t("training.endDebrief")}
            </Button>
          </>
        ) : phase === "done" ? (
          /* Phase 5 : session terminée → carte de relance (hors challenge) */
          renderPostSession()
        ) : null}
      </div>

      {/* Inactivity warning popup */}
      {showInactivityWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 max-w-sm mx-4 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">{t("training.inactivityTitle")}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t("training.inactivityMessage")}
            </p>
            <Button onClick={resetActivity} className="px-8">
              {t("training.inactivityContinue")}
            </Button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
